import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Alert, Platform, AppState, InteractionManager, type AppStateStatus } from 'react-native';
import * as Linking from 'expo-linking';
import { useUserStore } from '@/state/userStore';
import { fetchUserTrips, subscribeToUserTrips, testFirestoreConnectivity, getLastKnownTrips, getTripById, type Trip, type TripDestination } from '@/services/trips';
import {
  fetchTripItineraryItems,
  subscribeToTripItineraryItems,
  updateItineraryItem,
  type ItineraryItem,
} from '@/services/itinerary';
import { geocodeAddress, GEOCODE_RATE_LIMITED } from '@/services/maps';
import { getCachedTrips, getCachedTripData, itineraryItemsFromOfflineCache } from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { logger, isUiDebugLoggingEnabled } from '@/services/logger';
import {
  getCurrentLocation,
  watchLocation,
  type LocationCoordinates,
} from '@/services/location';
import {
  generateNavigationPrompt,
  type NavigationPrompt,
} from '@/services/navigation';
import {
  fetchLandmarks,
  getCachedLandmarks,
  updatePlacesCacheOnWifi,
  clearPlacesCache,
  type Place,
} from '@/services/places';
import {
  requestNotificationPermissions,
  sendNavigationPrompt,
  checkNearbyLandmarks,
  sendLandmarkNotification,
} from '@/services/notifications';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';
import { buildTripSelectOptions } from '@/utils/tripSelectOptions';
import { getDefaultTripId } from '@/utils/tripSelection';
import type { MapPin } from '@/components/maps/MapView';
import type { MapBounds } from '@/components/maps/MapView';
import type { GeocodeResult } from '@/services/maps';
import { getAndClearGeocodeCacheClearedFlag, getPersistedMapPins, setPersistedMapPins } from '@/utils/geocodeCache';
import { mergeItineraryPinsForActiveItems } from '@/features/maps/pinReconciliation';
import {
  DEFAULT_LANDMARK_DENSITY_PRESET,
  getLandmarkDensityPreset,
} from '@/features/maps/landmarkDensityPresets';
import {
  buildContextualSearchString,
  buildItineraryGeocodeQueryText,
  coordsWithinCountryBounds,
  enrichCityLabelWithCountryParts,
  getCountryCodeFromDestination,
  getCountryCodeForActivity,
  getDestinationCoordinates,
  isLikelyAdministrativeRegionQuery,
  marrakechAreaProximityOverrideForMorocco,
  resolveTripDestinationLabel,
  validateGeocodeResult,
  haversineDistance,
} from '@/utils/geocodeHelpers';
import { getDestinationForDate, getDestinationsForDate, toDateKey } from '@/utils/destinationDate';

export type TripMapMode = 'full' | 'preview';

export type UseTripMapDataOptions = {
  initialTripId?: string | null;
  controlledTripId?: string | null;
  onTripChange?: (tripId: string | null) => void;
  mode?: TripMapMode;
  isScreenActive?: boolean;
};

// In-memory cache to avoid showing "Loading itinerary..." on repeat visits.
// This is especially helpful when Firestore's first onSnapshot callback is delayed by network.
const itineraryMemoryCache = new Map<string, { items: ItineraryItem[]; updatedAt: number }>();
const ITINERARY_EMPTY_SNAPSHOT_GRACE_MS = 3500;

/**
 * Bump when itinerary map pin / geocode semantics change. Part of `geocodeKey` so a completed
 * run from older logic does not skip re-geocode when inputs are unchanged (`updatedAt` excluded).
 */
const ITINERARY_GEOCODE_PIPELINE_REVISION = '2026-05-24-admin-address-geocode-query';

/** Drop persisted pins and reject stored Firestore coords when farther than this from the trip anchor. */
const ITINERARY_PIN_MAX_KM_FROM_TRIP_ANCHOR = 250;

/** Must match `createItineraryPins` geocode eligibility (id + location text only; not coords/`updatedAt`). */
function isObviousNonPlaceTitleForEffect(title: string): boolean {
  const t = title.trim().toLowerCase();
  if (t.length < 4) return true;
  if (/^day\s*\d+$/i.test(t)) return true;
  if (/\b(free time|flexible|tbd|hotel check|check-?in|check-?out|travel day|rest day|departure|arrival)\b/i.test(t)) {
    return true;
  }
  const mealOnly = new Set([
    'lunch',
    'dinner',
    'breakfast',
    'brunch',
    'coffee',
    'tea',
    'snack',
    'drinks',
  ]);
  if (t.length <= 24 && mealOnly.has(t)) return true;
  return false;
}

/** Best-effort trip "home" coords for validating persisted pins when `destinationCenter` is not ready yet. */
function resolveTripMapAnchorCoords(params: {
  destinationCenter: LocationCoordinates | null;
  trip: Trip | null;
  tripDestinations: TripDestination[];
  itineraryItems: ItineraryItem[];
}): LocationCoordinates | null {
  const { destinationCenter, trip, tripDestinations, itineraryItems } = params;
  if (destinationCenter) return destinationCenter;

  const dc = trip?.destinationCoordinates;
  if (dc && Number.isFinite(dc.latitude) && Number.isFinite(dc.longitude)) {
    return { latitude: dc.latitude, longitude: dc.longitude };
  }

  const dest0 = trip?.destinations?.[0] ?? tripDestinations?.[0];
  const d0coords = dest0?.coordinates;
  if (d0coords && Number.isFinite(d0coords.latitude) && Number.isFinite(d0coords.longitude)) {
    return { latitude: d0coords.latitude, longitude: d0coords.longitude };
  }

  const tryLabel = (label: string | null | undefined): LocationCoordinates | null => {
    if (!label?.trim()) return null;
    const c = getDestinationCoordinates(label.trim());
    return c ? { latitude: c.lat, longitude: c.lng } : null;
  };

  if (dest0?.city) {
    const hit = tryLabel(dest0.city);
    if (hit) return hit;
  }

  const td0City = tripDestinations?.[0]?.city;
  if (td0City) {
    const hit = tryLabel(td0City);
    if (hit) return hit;
  }

  if (typeof trip?.destination === 'string' && trip.destination.trim()) {
    const first = trip.destination.split(',')[0]?.trim();
    const hit = tryLabel(first);
    if (hit) return hit;
  }

  if (typeof trip?.name === 'string' && trip.name.trim()) {
    const stripped = trip.name.replace(/\s+trip\s*$/i, '').trim();
    const first = stripped.split(',')[0]?.trim();
    if (first) {
      const hit = tryLabel(first);
      if (hit) return hit;
    }
    const hitFull = tryLabel(stripped);
    if (hitFull) return hitFull;
  }

  for (const it of itineraryItems) {
    const raw = (it.address || it.location || '').trim();
    if (!raw) continue;
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) continue;
    const cityIdx = parts.length >= 3 ? 1 : 0;
    const hit = tryLabel(parts[cityIdx]);
    if (hit) return hit;
  }

  return null;
}

function itemHasGeocodeSourceForEffect(item: ItineraryItem): boolean {
  const addr = (item.address || '').trim();
  const loc = (item.location || '').trim();
  if (addr.length > 0 || loc.length > 0) return true;
  const title = (item.title || '').trim();
  return title.length > 0 && !isObviousNonPlaceTitleForEffect(title);
}

/** Stable signature: restarting the geocode effect on every Firestore snapshot (coords/`updatedAt`) caused cleanup → false "repinning" loops. */
function buildItineraryGeocodeEffectKey(items: ItineraryItem[]): string {
  return items
    .filter(itemHasGeocodeSourceForEffect)
    .map((item) => {
      const q = buildItineraryGeocodeQueryText(item);
      return `${item.id}:${q}`;
    })
    .sort()
    .join('|');
}

function buildTripDestinationsGeocodeKey(destinations: TripDestination[]): string {
  return destinations
    .map((dest) => {
      const d = dest as any;
      const city = `${d?.city || ''}`.trim();
      const country = `${d?.country || ''}`.trim();
      const startDate = `${d?.startDate || ''}`.trim();
      const endDate = `${d?.endDate || ''}`.trim();
      return `${city}|${country}|${startDate}|${endDate}`;
    })
    .join(',');
}

export function useTripMapData({
  initialTripId = null,
  controlledTripId,
  onTripChange,
  mode = 'full',
  isScreenActive = true,
}: UseTripMapDataOptions = {}) {
  const landmarkDensityPreset = getLandmarkDensityPreset(DEFAULT_LANDMARK_DENSITY_PRESET);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [tripsReloadNonce, setTripsReloadNonce] = useState(0);
  const hasSeenNonEmptyTripsRef = useRef(false);
  const [tripLoadDebug, setTripLoadDebug] = useState<{
    uidPresent: boolean;
    cacheTripsLen: number | null;
    subscriptionTripsLen: number | null;
    fallbackTripsLen: number | null;
    lastSource:
      | 'none'
      | 'uid-missing'
      | 'uid-missing-grace'
      | 'uid-missing-cleared'
      | 'cache'
      | 'memory-cache'
      | 'subscription'
      | 'fallback-fetch'
      | 'watchdog';
    lastUpdatedAt: string | null;
    effectStartedAt: string | null;
    watchdogFiredAt: string | null;
    cacheAttemptedAt: string | null;
    cacheError: string | null;
    subscriptionAttemptedAt: string | null;
    subscriptionFirstCallbackAt: string | null;
    subscriptionError: string | null;
    fallbackAttemptedAt: string | null;
    fallbackResolvedAt: string | null;
    fallbackError: string | null;
    connectivityTestResult?: {
      success: boolean;
      latencyMs: number;
      error?: string;
    } | null;
  }>({
    uidPresent: false,
    cacheTripsLen: null,
    subscriptionTripsLen: null,
    fallbackTripsLen: null,
    lastSource: 'none',
    lastUpdatedAt: null,
    effectStartedAt: null,
    watchdogFiredAt: null,
    cacheAttemptedAt: null,
    cacheError: null,
    subscriptionAttemptedAt: null,
    subscriptionFirstCallbackAt: null,
    subscriptionError: null,
    fallbackAttemptedAt: null,
    fallbackResolvedAt: null,
    fallbackError: null,
  });
  const [selectedTripId, setSelectedTripIdState] = useState<string | null>(
    controlledTripId ?? initialTripId ?? null,
  );
  const selectionSourceRef = useRef<'controlled' | 'internal' | null>(null);
  const lastInternalSelectionRef = useRef<string | null>(null);

  const [itineraryItems, setItineraryItems] = useState<ItineraryItem[]>([]);
  /** Bumps the geocode effect when the user taps "Re-pin" — spreading `itineraryItems` alone does not change `itineraryGeocodeEffectKey`. */
  const [mapGeocodeRefreshNonce, setMapGeocodeRefreshNonce] = useState(0);
  const itineraryItemsRef = useRef<ItineraryItem[]>(itineraryItems);
  itineraryItemsRef.current = itineraryItems;
  const [tripSelectionSnapshot, setTripSelectionSnapshot] = useState<Trip[]>([]);
  const [selectedTripRecovery, setSelectedTripRecovery] = useState<Trip | null>(null);
  const [itineraryItemsLoading, setItineraryItemsLoading] = useState(false);
  const selectionRecoveryFetchKeyRef = useRef<string | null>(null);
  const itineraryItemsFirstCallbackRef = useRef(false);
  const itineraryLoadingStartedAtRef = useRef<number | null>(null);
  const itineraryLoadingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itineraryEmptySinceRef = useRef<number | null>(null);
  const hasSeenNonEmptyItineraryRef = useRef(false);
  const itineraryOnlineEmptyVerifyInFlightRef = useRef(false);
  const [appState, setAppState] = useState<AppStateStatus>(AppState.currentState);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);
  const lastKnownUidRef = useRef<string | null>(null);
  const uidMissingSinceRef = useRef<number | null>(null);
  const uidMissingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parseHHmmToMinutes = useCallback((value?: string | null): number | null => {
    if (!value) return null;
    const trimmed = value.trim();
    const m = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (!m) return null;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23) return null;
    if (mm < 0 || mm > 59) return null;
    return hh * 60 + mm;
  }, []);

  const pickDestinationForItemByTime = useCallback(
    (
      item: ItineraryItem,
      candidates: Array<{ destination: TripDestination; index: number }>,
    ): { destination: TripDestination; index: number } | null => {
      if (candidates.length <= 1) return candidates[0] ?? null;

      // Explicit override wins.
      if (typeof item.cityIndex === 'number') {
        const found = candidates.find((c) => c.index === item.cityIndex);
        if (found) return found;
      }

      const itemDayKey = toDateKey(item.startDate);
      if (!itemDayKey) return null;
      const itemMinutes = parseHHmmToMinutes(item.startTime);
      if (itemMinutes === null) return null;

      const inWindow: Array<{ destination: TripDestination; index: number }> = [];
      for (const c of candidates) {
        const startKey = toDateKey(c.destination.startDate);
        const endKey = toDateKey(c.destination.endDate);
        if (!startKey || !endKey) continue;

        const startMin = startKey === itemDayKey ? parseHHmmToMinutes(c.destination.startTime) ?? 0 : 0;
        const endMin = endKey === itemDayKey ? parseHHmmToMinutes(c.destination.endTime) ?? (24 * 60 - 1) : (24 * 60 - 1);

        // Handle pathological cases (e.g., endTime earlier than startTime on same day).
        const lo = Math.min(startMin, endMin);
        const hi = Math.max(startMin, endMin);

        if (itemMinutes >= lo && itemMinutes <= hi) {
          inWindow.push(c);
        }
      }

      if (inWindow.length === 0) return null;
      if (inWindow.length === 1) return inWindow[0];

      // Still ambiguous: prefer later destination (arrival city) on overlap days.
      return inWindow.reduce((acc, cur) => (cur.index > acc.index ? cur : acc));
    },
    [parseHHmmToMinutes],
  );

  const inferDestinationLabelFromItinerary = useCallback((items: ItineraryItem[]) => {
    if (!items.length) return undefined;

    const bad = /\b(area|district|region)\b/i;
    const stop = new Set(['hotel', 'airport', 'rest', 'free time', 'tbd']);

    // Reject itinerary prose that isn't a place name. Real place candidates are typically
    // short proper nouns ("Lima", "New York"). Pass through prose like "Ingreso por la frontera"
    // or "Entry through the border" would poison landmark fetches for the whole trip.
    const looksLikeProse = (value: string): boolean => {
      const trimmed = value.trim();
      if (!trimmed) return true;
      // Too many words → almost certainly prose, not a city.
      const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
      if (wordCount > 4) return true;
      // Contains typical lowercase articles/prepositions that never appear in a place label.
      const proseTokens =
        /(^|\s)(the|a|an|of|for|with|and|or|but|to|from|into|onto|through|over|under|between|within|without|before|after|during|por|la|el|los|las|en|de|del|para|con|sin|du|de la|le|les|et|ou|ohne|mit|durch|uber|unter|zwischen|vor|nach|bei)($|\s)/i;
      if (proseTokens.test(trimmed)) return true;
      // Contains a verb-ish lowercase leading token (e.g. "Entry through...", "Visit the...").
      const firstWord = trimmed.split(/\s+/)[0] || '';
      if (/^(entry|visit|tour|depart|arrive|check|drive|walk|board|explore|discover|transfer|pickup|drop|leave|return)/i.test(firstWord)) {
        return true;
      }
      return false;
    };

    const counts = new Map<string, number>();
    const bump = (value: string) => counts.set(value, (counts.get(value) ?? 0) + 1);

    for (const it of items) {
      const raw = (it.address || it.location || '').trim();
      if (!raw) continue;
      const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);

      let candidate: string | undefined;
      if (parts.length === 2) {
        // "Cairo, Egypt" => city
        candidate = parts[0];
      } else if (parts.length >= 3) {
        // "Wellington St, Ottawa, ON, Canada" => city
        candidate = parts[1];
      } else {
        candidate = parts[0];
      }

      if (!candidate) continue;
      const normalized = candidate.trim();
      const lower = normalized.toLowerCase();
      if (!normalized) continue;
      if (stop.has(lower)) continue;
      if (bad.test(lower)) continue;
      if (looksLikeProse(normalized)) continue;

      bump(normalized);
    }

    if (counts.size === 0) return undefined;

    // Only accept an inferred label if we can independently verify it resolves to a real place
    // (known city or country). This prevents handing back garbage labels like "Ingreso por la".
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    const strong = sorted.find(
      ([label]) => !!getDestinationCoordinates(label) || !!getCountryCodeFromDestination(label),
    );
    if (strong) return strong[0];
    return sorted[0]?.[0];
  }, []);

  const inferCityForLandmarks = useCallback(
    (items: ItineraryItem[]): string | undefined => {
      if (!items.length) return undefined;

      const stop = new Set(['hotel', 'airport', 'cai', 'rest', 'free time', 'tbd']);
      const badSuffix = /\b(area|district|region)\b/i;

      const candidates: string[] = [];
      for (const it of items) {
        const raw = `${it.address || ''}`.trim() || `${it.location || ''}`.trim();
        if (!raw) continue;
        const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
        const first = parts[0]?.trim();
        if (!first) continue;
        const lower = first.toLowerCase();
        if (stop.has(lower)) continue;
        if (badSuffix.test(lower)) continue;
        // If it's a street address ("Wellington St"), it's still okay but less ideal; we’ll collect and let other sources win.
        candidates.push(first);
      }

      // Prefer something that looks like a city we can map/validate.
      return candidates.find((c) => getDestinationCoordinates(c) || getCountryCodeFromDestination(c)) || candidates[0];
    },
    [],
  );
  const [currentLocation, setCurrentLocation] = useState<LocationCoordinates | null>(null);
  const currentLocationRef = useRef<LocationCoordinates | null>(null);
  currentLocationRef.current = currentLocation;
  const mapCenterStableRef = useRef<LocationCoordinates | null>(null);
  const [mapPins, setMapPins] = useState<MapPin[]>([]);
  const [landmarks, setLandmarks] = useState<Place[]>([]);
  const [rawLandmarks, setRawLandmarks] = useState<Place[]>([]);
  // Exposed for on-screen diagnostics so we can verify the landmark fetch used a sensible anchor.
  const [landmarkFetchProximity, setLandmarkFetchProximity] = useState<LocationCoordinates | null>(
    null,
  );
  const [landmarksReloadNonce, setLandmarksReloadNonce] = useState(0);
  const [landmarksRefreshing, setLandmarksRefreshing] = useState(false);
  const [landmarkSource, setLandmarkSource] = useState<'none' | 'cache' | 'live' | 'baseline'>('none');
  const landmarkRefreshPendingRef = useRef(false);
  /** Bumps on each landmark fetch effect run so stale async completions don't clear UI / manual-refresh state. */
  const landmarkFetchGenerationRef = useRef(0);
  /** Tracks `pinsLoading` to run deferred landmark reload once when pinning finishes (see effect below). */
  const prevPinsLoadingForLandmarkFetchRef = useRef<boolean | null>(null);
  /** Coalesce rapid `setLandmarksReloadNonce` calls (anchor retry + post-pinning bump + backfill timers). */
  const landmarkReloadCoalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** One coalesced "empty landmarks" auto-retry per trip+city (large trips were retriggering per pin). */
  const landmarkEmptyDiscoveryRetryScheduledRef = useRef<Set<string>>(new Set());
  const landmarkRelaxedFallbackUsedRef = useRef<Set<string>>(new Set());
  const landmarkLiveFetchAttemptedRef = useRef<Set<string>>(new Set());
  const landmarkAutoBackfillAttemptsRef = useRef<Map<string, number>>(new Map());
  const landmarkAutoBackfillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Geocode trip destination for map center when no pins available (must be declared before effects that reference it)
  const [destinationCenter, setDestinationCenter] = useState<LocationCoordinates | null>(null);
  const [previousDestinationCenter, setPreviousDestinationCenter] = useState<LocationCoordinates | null>(null);
  const [navigationPrompt, setNavigationPrompt] = useState<NavigationPrompt | null>(null);
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false); // Changed to false - don't auto-show
  const [showOfflineDownload, setShowOfflineDownload] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  type GeocodeStatus = 'idle' | 'running' | 'paused' | 'rate_limited';
  const [geocodeStatus, setGeocodeStatus] = useState<GeocodeStatus>('idle');
  const isGeocoding = geocodeStatus !== 'idle';
  const isGeocodingPaused = geocodeStatus === 'paused'; // True when waiting for app to be active (e.g. background)
  const isGeocodingRef = useRef(false);
  const [pinsProgress, setPinsProgress] = useState<{ total: number; processed: number }>({ total: 0, processed: 0 });
  const pinsProgressRef = useRef(pinsProgress);
  const geocodeRateLimited = geocodeStatus === 'rate_limited';
  const setIsGeocoding = (next: boolean) => {
    setGeocodeStatus((prev) => {
      if (!next) return 'idle';
      if (prev === 'paused' || prev === 'rate_limited') return prev;
      return 'running';
    });
  };
  const setIsGeocodingPaused = (paused: boolean) => {
    setGeocodeStatus((prev) => {
      if (paused) {
        return prev === 'idle' ? 'idle' : 'paused';
      }
      return prev === 'paused' ? 'running' : prev;
    });
  };
  const setGeocodeRateLimited = (limited: boolean) => {
    setGeocodeStatus((prev) => {
      if (limited) return 'rate_limited';
      return prev === 'rate_limited' ? 'running' : prev;
    });
  };
  const clearItineraryPinsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geocodePriorityDayKeyRef = useRef<string | null>(null);
  const geocodeScopeDayKeyRef = useRef<string | null>(null);
  /** Manual "Re-pin" must hit the geocoder, not the Firestore coordinate fast-path. */
  const forceFreshItineraryGeocodeRef = useRef(false);
  const geocodeAbortRef = useRef<AbortController | null>(null);
  const geocodeRunKeyRef = useRef<string | null>(null);
  const geocodeCompletedKeyRef = useRef<string | null>(null);
  const geocodeRunStartedAtRef = useRef<number | null>(null);
  const pinsProgressUpdatedAtRef = useRef<number>(Date.now());
  const lastDestinationsKeyRef = useRef<string | null>(null);
  const pinsLoadingWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geocodeNeedsRetryRef = useRef(false);
  const geocodeRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geocodeRetryAfterRef = useRef<number>(0);
  /** Mirrors mapPins so app-state effect can decide whether to re-geocode or just nudge (no deps). */
  const mapPinsRef = useRef<MapPin[]>([]);
  const geocodeFailureCountRef = useRef<number>(0);
  const geocodeFailureWindowStartedAtRef = useRef<number | null>(null);
  const geocodeCircuitOpenUntilRef = useRef<number>(0);
  /** Dedupe Firestore coordinate writes per item during a geocode run. */
  const persistedMapCoordsItemIdsRef = useRef<Set<string>>(new Set());
  /** Collected coordinates to persist after the geocode run completes (avoids mid-run Firestore writes). */
  const pendingCoordPersistsRef = useRef<Array<{ items: ItineraryItem[]; coords: LocationCoordinates }>>([]);

  // Track app state so we can avoid clearing UI state during iOS "inactive" transitions
  // (e.g. Siri, app switcher). iOS may briefly pause JS and auth observers.
  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      appStateRef.current = next;
      setAppState(next);
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    isGeocodingRef.current = isGeocoding;
  }, [isGeocoding]);

  useEffect(() => {
    pinsProgressRef.current = pinsProgress;
  }, [pinsProgress]);

  useEffect(() => {
    mapPinsRef.current = mapPins;
  }, [mapPins]);

  const destinationCenterRef = useRef<LocationCoordinates | null>(null);
  useEffect(() => {
    destinationCenterRef.current = destinationCenter;
  }, [destinationCenter]);

  // Declared before landmark/pin effects that reference it (avoids TDZ: "Cannot access before initialization").
  const pinsLoading = useMemo(() => {
    if (tripsLoading) return true;
    if (itineraryItemsLoading) return true;
    if (isGeocoding) return true;
    if (pinsProgress.total > 0 && pinsProgress.processed < pinsProgress.total) return true;
    return false;
  }, [isGeocoding, itineraryItemsLoading, pinsProgress.processed, pinsProgress.total, tripsLoading]);

  const logIosGeocode = useCallback(
    (event: string, details?: Record<string, unknown>) => {
      if (Platform.OS !== 'ios') return;
      const payload = details ?? {};
      // Use warn-level logging so iOS Console captures it in release/preview builds.
      console.warn(`[TripMap][iOS] ${event}`, payload);
      logger.warn(`[TripMap][iOS] ${event}`, payload);
    },
    [],
  );

  const hasSelectedTrip = Boolean(selectedTripId);
  // Keep the long-lived location watcher stable during transient iOS "inactive" phases,
  // but tear it down once the app is fully backgrounded OR this map instance is not active.
  const enableLocation =
    mode === 'full' &&
    hasSelectedTrip &&
    isScreenActive &&
    appState !== 'background';
  // Prompts/notification checks should only run while the full map is actively visible.
  const enablePrompts =
    mode === 'full' &&
    hasSelectedTrip &&
    isScreenActive &&
    appState === 'active';

  const fullTripBounds = useMemo<MapBounds | null>(() => {
    const pinsForBounds = mapPins.filter((p) => p.type !== 'user' && p.coordinates);
    if (pinsForBounds.length < 2) return null;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    pinsForBounds.forEach((pin) => {
      minLat = Math.min(minLat, pin.coordinates.latitude);
      maxLat = Math.max(maxLat, pin.coordinates.latitude);
      minLng = Math.min(minLng, pin.coordinates.longitude);
      maxLng = Math.max(maxLng, pin.coordinates.longitude);
    });

    if (!Number.isFinite(minLat) || !Number.isFinite(maxLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLng)) {
      return null;
    }

    return {
      southWest: { latitude: minLat, longitude: minLng },
      northEast: { latitude: maxLat, longitude: maxLng },
    };
  }, [mapPins]);

  const setSelectedTripId = useCallback(
    (tripId: string | null) => {
      selectionSourceRef.current = 'internal';
      lastInternalSelectionRef.current = tripId;
      setSelectedTripIdState(tripId);
    },
    [],
  );

  // Sync with controlled trip ID (e.g., from router params)
  // Only sync if the change is from an external source (not from internal selection)
  useEffect(() => {
    // Skip if this change was triggered by an internal selection
    // Check both the ref and if controlledTripId matches our last internal selection
    if (selectionSourceRef.current === 'internal') {
      return;
    }

    // If controlledTripId matches what we just selected internally, don't override
    // This handles the case where router params update after our internal selection
    if (controlledTripId === lastInternalSelectionRef.current) {
      return;
    }

    // Only sync if controlledTripId is different and not null
    // This prevents overriding user selections
    if (controlledTripId && controlledTripId !== selectedTripId) {
      selectionSourceRef.current = 'controlled';
      setSelectedTripIdState(controlledTripId);
    }
  }, [controlledTripId, selectedTripId]);

  // Notify parent when internal selection changes
  useEffect(() => {
    if (selectionSourceRef.current === 'internal') {
      onTripChange?.(selectedTripId);
      // Clear the ref after notifying
      selectionSourceRef.current = null;
    }
  }, [selectedTripId, onTripChange]);

  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTripSelectionSnapshot([]);
      return;
    }
    if (!Array.isArray(trips) || trips.length === 0) {
      return;
    }
    setTripSelectionSnapshot((prev) => {
      const byId = new Map<string, Trip>();
      prev.forEach((trip) => {
        if (trip?.id) byId.set(trip.id, trip);
      });
      trips.forEach((trip) => {
        if (trip?.id) byId.set(trip.id, trip);
      });
      const next = Array.from(byId.values());
      if (next.length === prev.length) {
        const prevKey = prev.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
        const nextKey = next.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
        if (prevKey === nextKey) return prev;
      }
      return next;
    });
  }, [firebaseUser?.uid, trips]);

  // Trip picker resilience: Firestore can occasionally emit a transient subset while listeners warm.
  // Keep selection/options stable by merging with last-known trips when live count shrinks.
  const tripsForSelection = useMemo(() => {
    const uid = firebaseUser?.uid;
    if (!uid) return trips;
    const lastKnownTrips = getLastKnownTrips(uid)?.trips ?? [];
    const byId = new Map<string, Trip>();
    tripSelectionSnapshot.forEach((trip) => {
      if (trip?.id) byId.set(trip.id, trip);
    });
    lastKnownTrips.forEach((trip) => {
      if (trip?.id) byId.set(trip.id, trip);
    });
    trips.forEach((trip) => {
      if (trip?.id) byId.set(trip.id, trip);
    });
    if (selectedTripRecovery?.id) {
      byId.set(selectedTripRecovery.id, selectedTripRecovery);
    }
    return Array.from(byId.values());
  }, [firebaseUser?.uid, trips, tripSelectionSnapshot, selectedTripRecovery]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || tripsLoading) return;
    if (tripsForSelection.length >= 2) return;
    const key = `${uid}:${tripsForSelection.length}`;
    if (selectionRecoveryFetchKeyRef.current === key) return;
    selectionRecoveryFetchKeyRef.current = key;
    let cancelled = false;
    void fetchUserTrips(uid, 100)
      .then(({ trips: fetchedTrips }) => {
        if (cancelled || !Array.isArray(fetchedTrips) || fetchedTrips.length === 0) return;
        setTripSelectionSnapshot((prev) => {
          const byId = new Map<string, Trip>();
          prev.forEach((trip) => {
            if (trip?.id) byId.set(trip.id, trip);
          });
          fetchedTrips.forEach((trip) => {
            if (trip?.id) byId.set(trip.id, trip);
          });
          return Array.from(byId.values());
        });
      })
      .catch(() => {
        // Best-effort recovery only.
      });
    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, tripsLoading, tripsForSelection.length]);

  useEffect(() => {
    if (!selectedTripId) {
      setSelectedTripRecovery(null);
      return;
    }
    const exists = tripsForSelection.some((trip) => trip.id === selectedTripId);
    if (exists) {
      setSelectedTripRecovery((prev) => (prev?.id === selectedTripId ? null : prev));
      return;
    }
    let cancelled = false;
    void getTripById(selectedTripId)
      .then((trip) => {
        if (cancelled || !trip?.id) return;
        setSelectedTripRecovery(trip);
        setTripSelectionSnapshot((prev) => {
          const byId = new Map<string, Trip>();
          prev.forEach((entry) => {
            if (entry?.id) byId.set(entry.id, entry);
          });
          byId.set(trip.id, trip);
          return Array.from(byId.values());
        });
      })
      .catch(() => {
        // Best-effort recovery only.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTripId, tripsForSelection]);

  const selectedTrip = useMemo(() => {
    return tripsForSelection.find((trip) => trip.id === selectedTripId) || null;
  }, [selectedTripId, tripsForSelection]);
  const selectedTripRef = useRef(selectedTrip);
  selectedTripRef.current = selectedTrip;

  // Cross-trip hygiene: when the selected trip changes, drop stale trip-scoped state
  // (landmarks + itinerary/landmark map pins from the previous trip) BEFORE the new trip's
  // data arrives. Prevents scenarios like opening "Lima, Peru" and briefly seeing
  // pins/landmarks from the previously viewed New York trip while the new data loads.
  const previousSelectedTripIdRef = useRef<string | null>(selectedTripId);
  useEffect(() => {
    const prev = previousSelectedTripIdRef.current;
    if (prev === selectedTripId) return;
    previousSelectedTripIdRef.current = selectedTripId;
    landmarkEmptyDiscoveryRetryScheduledRef.current.clear();
    landmarkRelaxedFallbackUsedRef.current.clear();
    landmarkAutoBackfillAttemptsRef.current.clear();
    if (landmarkAutoBackfillTimerRef.current) {
      clearTimeout(landmarkAutoBackfillTimerRef.current);
      landmarkAutoBackfillTimerRef.current = null;
    }
    prevPinsLoadingForLandmarkFetchRef.current = null;
    if (landmarkReloadCoalesceTimerRef.current) {
      clearTimeout(landmarkReloadCoalesceTimerRef.current);
      landmarkReloadCoalesceTimerRef.current = null;
    }
    // Allow the destinations effect to run a fresh refresh for the newly selected trip.
    lastDestinationsKeyRef.current = null;
    setLandmarkSource('none');
    if (prev === null && selectedTripId) {
      // first selection; nothing stale to clear
      return;
    }
    setRawLandmarks([]);
    setLandmarks([]);
    setMapPins((prevPins) =>
      prevPins.filter((p) => {
        if (p.type === 'landmark') return false;
        if (p.type === 'itinerary') {
          return selectedTripId ? p.tripId === selectedTripId : false;
        }
        return true;
      }),
    );
  }, [selectedTripId]);

  const isBaselineSeeded = useCallback(
    (places: Place[]): boolean =>
      places.length > 0 && places.every((p) => !!p.tags?.includes('baseline-seeded')),
    [],
  );

  const refreshPins = useCallback(
    (
      priorityDayKey?: string | null,
      options?: { onlyPriorityDay?: boolean; forceFreshGeocode?: boolean },
    ) => {
    // Manual "Re-pin" (forceFreshGeocode): bypass persisted restore + Firestore coord fast-path.
    if (options?.forceFreshGeocode) {
      forceFreshItineraryGeocodeRef.current = true;
    }
    // Force re-geocode on next run (e.g. after cache clear) by clearing completed/run keys.
    geocodeCompletedKeyRef.current = null;
    geocodeRunKeyRef.current = null;
    // Allow the map screen to hint which day to prioritize for faster perceived loading.
    geocodePriorityDayKeyRef.current = priorityDayKey ?? null;
    // Optional: scope this refresh to the selected day only (used by day view manual refresh).
    geocodeScopeDayKeyRef.current =
      options?.onlyPriorityDay && priorityDayKey ? priorityDayKey : null;
    setMapGeocodeRefreshNonce((n) => n + 1);
    // Nudge list identity for any downstream memo that keys off array reference.
    setItineraryItems((items) => [...items]);
    },
    [],
  );

  // Lightweight nudge to force native map to re-render with current pins (no re-geocode).
  // Use when map tab gains focus or app becomes active so pins show without app switch.
  const nudgePinsForDisplay = useCallback(() => {
    setMapPins((prev) => (prev.length ? [...prev] : prev));
  }, []);

  const scheduleGeocodeRetry = useCallback((reason: string) => {
    const now = Date.now();
    if (geocodeCircuitOpenUntilRef.current && now < geocodeCircuitOpenUntilRef.current) {
      if (__DEV__) {
        const remainingMs = geocodeCircuitOpenUntilRef.current - now;
        console.warn('[TripMap] Geocode retry blocked by circuit breaker', { reason, remainingMs });
      }
      geocodeNeedsRetryRef.current = false;
      return;
    }

    geocodeNeedsRetryRef.current = true;
    if (geocodeRetryTimerRef.current) return;

    const earliest = geocodeRetryAfterRef.current;
    const delay = Math.max(2000, earliest > now ? earliest - now : 0);

    geocodeRetryTimerRef.current = setTimeout(() => {
      geocodeRetryTimerRef.current = null;
      if (appStateRef.current !== 'active') {
        geocodeNeedsRetryRef.current = true;
        return;
      }
      const currentNow = Date.now();
      if (geocodeCircuitOpenUntilRef.current && currentNow < geocodeCircuitOpenUntilRef.current) {
        if (__DEV__) {
          const remainingMs = geocodeCircuitOpenUntilRef.current - currentNow;
          console.warn('[TripMap] Geocode retry blocked by circuit breaker', { reason, remainingMs });
        }
        geocodeNeedsRetryRef.current = false;
        return;
      }
      if (geocodeRunKeyRef.current) {
        geocodeNeedsRetryRef.current = true;
        scheduleGeocodeRetry('run-in-progress');
        return;
      }
      geocodeNeedsRetryRef.current = false;
      geocodeRetryAfterRef.current = Date.now() + 10_000;
      if (__DEV__) {
        console.warn('[TripMap] Retrying geocode after transient failure', { reason });
      }
      refreshPins();
    }, delay);
  }, [refreshPins]);

  useEffect(() => {
    if (appState !== 'active') {
      if (isGeocodingRef.current) {
        setIsGeocodingPaused(true);
        geocodeNeedsRetryRef.current = true;
        try {
          geocodeAbortRef.current?.abort();
        } catch {
          // ignore
        }
      }
      return;
    }

    setIsGeocodingPaused(false);
    // If we already have itinerary pins, do NOT trigger full re-geocode (avoids "Pinning locations…" again).
    const hasItineraryPins = mapPinsRef.current.some((p) => p.type === 'itinerary');
    if (hasItineraryPins) {
      geocodeNeedsRetryRef.current = false;
    } else if (geocodeNeedsRetryRef.current) {
      scheduleGeocodeRetry('app-active');
    }
    // Unblock "Loading itinerary" from cache when app becomes active so Maps doesn’t stay stuck.
    if (selectedTripId && itineraryItemsLoading) {
      const cached = itineraryMemoryCache.get(selectedTripId);
      if (cached) {
        setItineraryItems(cached.items);
        setItineraryItemsLoading(false);
        itineraryItemsFirstCallbackRef.current = true;
      }
    }
    // When app becomes active, nudge map to repaint so pins show without switching apps.
    const repaintTimer = setTimeout(() => nudgePinsForDisplay(), 300);
    return () => clearTimeout(repaintTimer);
  }, [appState, scheduleGeocodeRetry, nudgePinsForDisplay, selectedTripId, itineraryItemsLoading]);

  useEffect(() => {
    return () => {
      if (geocodeRetryTimerRef.current) {
        clearTimeout(geocodeRetryTimerRef.current);
        geocodeRetryTimerRef.current = null;
      }
    };
  }, []);

  // Multi-city support
  const tripDestinations = useMemo(() => selectedTrip?.destinations ?? [], [selectedTrip?.destinations]);
  const tripDestinationsRef = useRef(tripDestinations);
  tripDestinationsRef.current = tripDestinations;
  const isMultiCityTrip = tripDestinations.length > 1;
  const [selectedCityIndex, setSelectedCityIndex] = useState(0);

  useEffect(() => {
    if (tripDestinations.length === 0) {
      if (selectedCityIndex !== 0) setSelectedCityIndex(0);
      return;
    }
    if (selectedCityIndex >= tripDestinations.length) {
      setSelectedCityIndex(tripDestinations.length - 1);
    }
  }, [selectedCityIndex, tripDestinations.length]);

  const selectedCityDestination = tripDestinations[selectedCityIndex] ?? null;

  const tripDestinationsGeocodeKey = useMemo(
    () => buildTripDestinationsGeocodeKey(tripDestinations),
    [tripDestinations],
  );
  const itineraryGeocodeEffectKey = useMemo(
    () => buildItineraryGeocodeEffectKey(itineraryItems),
    [itineraryItems],
  );
  /** Inputs to `annotatePlacesForTrip` only (titles + day keys). Coords/`updatedAt` churn must not remerge landmarks. */
  const itineraryLandmarkAnnotationKey = useMemo(
    () =>
      itineraryItems
        .map((item) => {
          const title = `${item.title || ''}`.trim().toLowerCase();
          const dk = toDateKey(item.startDate) || '';
          return `${item.id}:${title}:${dk}`;
        })
        .sort()
        .join('|'),
    [itineraryItems],
  );
  const tripLandmarkAnnotationKey = useMemo(() => {
    if (!selectedTrip) return '';
    const s = selectedTrip.startDate;
    const e = selectedTrip.endDate;
    const sIso = s instanceof Date ? s.toISOString() : `${s ?? ''}`;
    const eIso = e instanceof Date ? e.toISOString() : `${e ?? ''}`;
    return `${selectedTrip.id}|${sIso}|${eIso}`;
  }, [selectedTrip]);

  // When user cleared geocode cache (e.g. from Settings), force re-geocode on next map visit.
  useEffect(() => {
    if (!selectedTripId) return;
    let mounted = true;
    getAndClearGeocodeCacheClearedFlag()
      .then((cleared) => {
        if (!mounted) return;
        if (cleared) refreshPins();
      })
      .catch((error) => {
        logger.warn(
          '[useTripMapData] Failed to read geocode-cache-cleared flag',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      });
    return () => {
      mounted = false;
    };
  }, [selectedTripId, refreshPins]);

  // Re-geocode itinerary pins when destinations change (date ranges or cities updated).
  // Debounce: trips often receive several Firestore snapshots while fields hydrate; each
  // change used to call refreshPins immediately and could restart pinning 2–3 times in a row.
  useEffect(() => {
    if (!tripDestinations.length) return;
    const destinationsKey = tripDestinations
      .map((dest) => {
        const city = (dest as any)?.city || '';
        const country = (dest as any)?.country || '';
        const startDate = (dest as any)?.startDate || '';
        const endDate = (dest as any)?.endDate || '';
        return `${city}|${country}|${startDate}|${endDate}`;
      })
      .join(',');

    if (lastDestinationsKeyRef.current === destinationsKey) {
      return;
    }

    const apply = () => {
      if (lastDestinationsKeyRef.current === destinationsKey) return;
      lastDestinationsKeyRef.current = destinationsKey;
      refreshPins();
    };

    // After a trip switch we reset lastDestinationsKeyRef. Seed it from the current snapshot
    // without refreshPins — TripMapExperience already calls refreshPins on selectedTripId change,
    // and the geocode effect re-runs when `tripDestinations` / itinerary updates.
    if (lastDestinationsKeyRef.current == null) {
      lastDestinationsKeyRef.current = destinationsKey;
      return;
    }

    const debounceMs = 450;
    const t = setTimeout(apply, debounceMs);
    return () => clearTimeout(t);
  }, [tripDestinations, refreshPins]);

  // Check download status when trip changes
  useEffect(() => {
    const checkDownloadStatus = async () => {
      if (Platform.OS === 'web' || !selectedTrip?.destination) {
        setIsDownloaded(false);
        return;
      }

      try {
        const { getDownloadedRegions, findDownloadedRegionForCity } = await import('@/services/offlineMaps');
        const regions = await getDownloadedRegions();
        const region = findDownloadedRegionForCity(regions, selectedTrip.destination);
        setIsDownloaded(!!region);
      } catch (error) {
        console.error('[useTripMapData] Error checking download status:', error);
        setIsDownloaded(false);
      }
    };

    checkDownloadStatus();
  }, [selectedTrip?.destination]);

  // Pre-cache common destinations on app start (non-blocking)
  useEffect(() => {
    // Avoid background geocoding bursts in production builds (can cause memory/CPU spikes on iOS).
    if (!__DEV__) return;
    const precacheCommonDestinations = async () => {
      // Only precache in background, don't block UI
      const commonCities = [
        'New York', 'London', 'Paris', 'Tokyo', 'Sydney',
        'Rome', 'Barcelona', 'Amsterdam', 'Berlin', 'Dubai'
      ];

      try {
        const { geocodeAddress } = await import('@/services/maps');
        // Precache in background with delay to not impact startup
        setTimeout(() => {
          void (async () => {
            try {
              for (const city of commonCities) {
                try {
                  // This will use cache if available, or fetch and cache if not
                  await geocodeAddress(city);
                } catch (error) {
                  // Ignore per-city errors, continue with other cities
                }
              }
            } catch (error) {
              logger.warn(
                '[useTripMapData] Precache common destinations loop failed',
                undefined,
                error instanceof Error ? error : new Error(String(error)),
              );
            }
          })();
        }, 2000); // Wait 2 seconds after app start
      } catch (error) {
        // Ignore precache errors
      }
    };

    precacheCommonDestinations();
  }, []);

  // Subscribe to trips
  useEffect(() => {
    if (!firebaseUser?.uid) {
      // On iOS, during app-switcher snapshotting / background transitions, auth state can briefly
      // appear "missing". If we immediately clear `trips` and mark loading complete, the UI can
      // flash "No trips yet" even though the user has active trips.
      //
      // Strategy:
      // - If we've ever seen trips in this session, keep the last list and show loading for a short grace window.
      // - If uid never returns (true sign-out), then clear after the grace window.
      // If we've ever had trips, DO NOT clear them on transient auth/app-state glitches.
      // iOS can briefly report auth as missing when the app becomes "inactive" (Siri, app switcher).
      if (hasSeenNonEmptyTripsRef.current || trips.length > 0) {
        setTripsLoading(true);
        setTripLoadDebug((prev) => ({
          ...prev,
          uidPresent: false,
          lastSource: 'uid-missing-grace',
          lastUpdatedAt: new Date().toISOString(),
          effectStartedAt: new Date().toISOString(),
          watchdogFiredAt: null,
          cacheAttemptedAt: null,
          cacheError: null,
          subscriptionAttemptedAt: null,
          subscriptionFirstCallbackAt: null,
          subscriptionError: null,
          fallbackAttemptedAt: null,
          fallbackResolvedAt: null,
          fallbackError: null,
        }));

        // Only clear after a *long* continuous missing-uid window, and only while active.
        // This prevents the "No trips" flicker when the app becomes inactive briefly.
        if (uidMissingSinceRef.current == null) {
          uidMissingSinceRef.current = Date.now();
        }
        if (!uidMissingTimerRef.current) {
          uidMissingTimerRef.current = setTimeout(() => {
            const stillMissing = !useUserStore.getState().firebaseUser?.uid;
            const shouldClear =
              stillMissing && AppState.currentState === 'active' && (uidMissingSinceRef.current != null);
            if (shouldClear) {
              setTrips([]);
              setTripsLoading(false);
              setTripLoadDebug((prev) => ({
                ...prev,
                uidPresent: false,
                lastSource: 'uid-missing-cleared',
                lastUpdatedAt: new Date().toISOString(),
              }));
            }
            uidMissingTimerRef.current = null;
            uidMissingSinceRef.current = null;
          }, 30_000);
        }

        return () => {
          // Keep the long-clear timer alive across rerenders; we'll clear it when uid returns.
        };
      }

      setTrips([]);
      setTripsLoading(false);
      setTripLoadDebug((prev) => ({
        ...prev,
        uidPresent: false,
        lastSource: 'uid-missing',
        lastUpdatedAt: new Date().toISOString(),
        effectStartedAt: new Date().toISOString(),
        watchdogFiredAt: null,
        cacheAttemptedAt: null,
        cacheError: null,
        subscriptionAttemptedAt: null,
        subscriptionFirstCallbackAt: null,
        subscriptionError: null,
        fallbackAttemptedAt: null,
        fallbackResolvedAt: null,
        fallbackError: null,
      }));
      return;
    }

    // uid present - clear any pending "uid missing" timer and remember uid.
    lastKnownUidRef.current = firebaseUser.uid;
    if (uidMissingTimerRef.current) {
      clearTimeout(uidMissingTimerRef.current);
      uidMissingTimerRef.current = null;
    }
    uidMissingSinceRef.current = null;

    setTripsLoading(true);
    setTripLoadDebug((prev) => ({
      ...prev,
      uidPresent: true,
      // Reset stage signals each time we resubscribe so debug is meaningful per attempt.
      cacheTripsLen: null,
      subscriptionTripsLen: null,
      fallbackTripsLen: null,
      lastSource: prev.lastSource === 'none' ? 'none' : prev.lastSource,
      lastUpdatedAt: prev.lastUpdatedAt,
      effectStartedAt: new Date().toISOString(),
      watchdogFiredAt: null,
      cacheAttemptedAt: null,
      cacheError: null,
      subscriptionAttemptedAt: null,
      subscriptionFirstCallbackAt: null,
      subscriptionError: null,
      fallbackAttemptedAt: null,
      fallbackResolvedAt: null,
      fallbackError: null,
      connectivityTestResult: null,
    }));

    const lastKnown = getLastKnownTrips(firebaseUser.uid);
    if (lastKnown?.trips?.length) {
      setTrips(lastKnown.trips);
      setTripsLoading(false);
      hasSeenNonEmptyTripsRef.current = true;
      setTripLoadDebug((prev) => ({
        ...prev,
        lastSource: 'memory-cache',
        lastUpdatedAt: new Date().toISOString(),
      }));
    }

    // PRE-FLIGHT: Test Firestore connectivity before setting up subscriptions
    // This helps diagnose cases where subscriptions never fire
    // MOVED: We now delay this check to avoid blocking the main thread/Firestore lock during initial subscription.
    // console.log('[useTripMapData] 🔵 Starting pre-flight Firestore connectivity test...');
    // testFirestoreConnectivity(firebaseUser.uid).then((result) => { ... });

    // Seed UI from cached trips (if available) so we don't flash "No trips yet" on cold start.
    // This is best-effort and does not replace the live Firestore subscription.
    let cancelled = false;
    let watchdogCleared = false;
    let didKickFallbackFetch = false;
    let hasSeenSubscriptionCallback = false;

    // If we already have cached/non-empty trips in memory, avoid clobbering them with a transient
    // empty snapshot during Firestore warm-up.
    hasSeenNonEmptyTripsRef.current = trips.length > 0;
    const watchdog = setTimeout(() => {
      // If Firestore never calls back (e.g., permissions/network), don't leave the UI stuck forever.
      // Keep this bounded; users perceive anything >~10s as "broken". (Reduced from 20s to 10s)
      if (!cancelled && !watchdogCleared) {
        console.warn('[useTripMapData] Watchdog triggered - forcing tripsLoading=false');
        setTripsLoading(false);
        setTripLoadDebug((prev) => ({
          ...prev,
          lastSource: 'watchdog',
          lastUpdatedAt: new Date().toISOString(),
          watchdogFiredAt: new Date().toISOString(),
          // Help diagnose when Firestore never invokes the callback at all.
          subscriptionError:
            prev.subscriptionFirstCallbackAt ? prev.subscriptionError : (prev.subscriptionError || 'no_callback'),
        }));

        // Run connectivity test now that we've timed out, to diagnose the failure.
        // We do this late to avoid contending with the initial subscription.
        console.log('[useTripMapData] 🔵 Watchdog firing connectivity test...');
        testFirestoreConnectivity(firebaseUser.uid)
          .then((result) => {
            console.log(`[useTripMapData] Watchdog connectivity check: ${result.success ? 'PASSED' : 'FAILED'} (${result.latencyMs}ms)`);
            setTripLoadDebug((prev) => ({
              ...prev,
              connectivityTestResult: result,
            }));
          })
          .catch((error) => {
            logger.warn(
              '[useTripMapData] Watchdog connectivity test threw',
              undefined,
              error instanceof Error ? error : new Error(String(error)),
            );
          });
      }
    }, 10000);

    // Load cached trips with proper error handling
    setTripLoadDebug((prev) => ({
      ...prev,
      cacheAttemptedAt: new Date().toISOString(),
    }));
    getCachedTrips(firebaseUser.uid)
      .then((cached) => {
        if (cancelled) return;
        // If the Firestore subscription has already produced a callback, don't let cache overwrite live data.
        if (hasSeenSubscriptionCallback) return;
        if (Array.isArray(cached) && cached.length > 0) {
          setTrips((prev) => (prev.length > 0 ? prev : cached));
          setTripsLoading(false);
          hasSeenNonEmptyTripsRef.current = true;
          setTripLoadDebug((prev) => ({
            ...prev,
            cacheTripsLen: cached.length,
            lastSource: 'cache',
            lastUpdatedAt: new Date().toISOString(),
          }));
          // Clear watchdog early if we got cached data
          watchdogCleared = true;
          clearTimeout(watchdog);
        }
      })
      .catch((err) => {
        // Don't let cache errors block the UI
        console.warn('[useTripMapData] getCachedTrips failed:', err);
        setTripLoadDebug((prev) => ({
          ...prev,
          cacheError: `${(err as any)?.code || ''} ${(err as any)?.message || String(err)}`.trim(),
          lastUpdatedAt: new Date().toISOString(),
        }));
        if (!__DEV__) {
          logger.warn('[useTripMapData] getCachedTrips failed', err as any);
        }
      });

    // Fallback: if Firestore subscriptions are slow/flaky on certain devices/networks,
    // do a one-shot fetch and unblock the UI. This is especially helpful when users
    // see "Loading trips..." forever or get an incorrect initial empty state.
    // Reduced from 6s to 3s for faster fallback when subscription is slow.
    const fallbackFetchTimer = setTimeout(() => {
      if (cancelled) return;
      if (didKickFallbackFetch) return;
      didKickFallbackFetch = true;
      setTripLoadDebug((prev) => ({
        ...prev,
        fallbackAttemptedAt: new Date().toISOString(),
      }));
      const timeoutMs = 10000; // Also reduced from 15s to 10s
      const timeout = new Promise<{ trips: Trip[] }>((_, reject) => {
        const t = setTimeout(() => {
          clearTimeout(t);
          const e: any = new Error(`fetchUserTrips timed out after ${timeoutMs}ms`);
          e.code = 'timeout';
          reject(e);
        }, timeoutMs);
      });

      Promise.race([fetchUserTrips(firebaseUser.uid), timeout])
        .then(({ trips: fetched }) => {
          if (cancelled) return;
          setTripLoadDebug((prev) => ({
            ...prev,
            fallbackTripsLen: Array.isArray(fetched) ? fetched.length : null,
            fallbackResolvedAt: new Date().toISOString(),
          }));
          // Unblock UI once fallback returns (even if empty).
          setTripsLoading(false);
          if (Array.isArray(fetched) && fetched.length > 0) {
            setTrips(fetched);
            hasSeenNonEmptyTripsRef.current = true;
            setTripLoadDebug((prev) => ({
              ...prev,
              lastSource: 'fallback-fetch',
              lastUpdatedAt: new Date().toISOString(),
            }));
            if (!watchdogCleared) {
              watchdogCleared = true;
              clearTimeout(watchdog);
            }
          }
        })
        .catch((err) => {
          if (__DEV__) {
            console.warn('[useTripMapData] fetchUserTrips fallback failed:', err);
          }
          setTripLoadDebug((prev) => ({
            ...prev,
            fallbackError: `${(err as any)?.code || ''} ${(err as any)?.message || String(err)}`.trim(),
            fallbackResolvedAt: new Date().toISOString(),
            lastUpdatedAt: new Date().toISOString(),
          }));
          // Ensure UI isn't left spinning if fallback fails and Firestore never calls back.
          setTripsLoading(false);
          if (!__DEV__) {
            logger.warn('[useTripMapData] fetchUserTrips fallback failed', err as any);
          }
        });
    }, 3000);

    setTripLoadDebug((prev) => ({
      ...prev,
      subscriptionAttemptedAt: new Date().toISOString(),
    }));

    let unsubscribe: () => void = () => { };
    try {
      unsubscribe = subscribeToUserTrips(
        firebaseUser.uid,
        (userTrips) => {
          if (!hasSeenSubscriptionCallback) {
            hasSeenSubscriptionCallback = true;
            setTripLoadDebug((prev) => ({
              ...prev,
              subscriptionFirstCallbackAt: new Date().toISOString(),
            }));
          }
          // Clear watchdog on successful callback
          if (!watchdogCleared) {
            watchdogCleared = true;
            clearTimeout(watchdog);
          }
          setTripLoadDebug((prev) => ({
            ...prev,
            subscriptionTripsLen: Array.isArray(userTrips) ? userTrips.length : null,
          }));
          if (Array.isArray(userTrips) && userTrips.length > 0) {
            hasSeenNonEmptyTripsRef.current = true;
            // CRITICAL: Compare trips before updating to prevent cascading effect runs
            setTrips((prev) => {
              if (prev.length !== userTrips.length) return userTrips;
              const prevKey = prev.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
              const newKey = userTrips.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
              return prevKey === newKey ? prev : userTrips;
            });
            setTripLoadDebug((prev) => ({
              ...prev,
              lastSource: 'subscription',
              lastUpdatedAt: new Date().toISOString(),
            }));
          } else {
            // Avoid overwriting cached trips with a transient empty list during warm-up.
            setTrips((prev) => {
              if (prev.length > 0 && !hasSeenNonEmptyTripsRef.current) {
                return prev;
              }
              // Also compare here to avoid unnecessary updates
              if (prev.length === userTrips.length) {
                const prevKey = prev.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
                const newKey = userTrips.map((t) => `${t.id}:${t.updatedAt || ''}`).join(',');
                if (prevKey === newKey) return prev;
              }
              return userTrips;
            });
          }
          setTripsLoading(false);
        },
        {
          onError: (err) => {
            const msg = `${(err as any)?.code || ''} ${(err as any)?.message || String(err)}`.trim();
            setTripLoadDebug((prev) => ({
              ...prev,
              subscriptionError: msg || prev.subscriptionError || 'subscription_error',
              lastUpdatedAt: new Date().toISOString(),
            }));
          },
          // Emit an initial callback right away so Maps doesn't sit in "no_callback".
          bootstrapDelayMs: 0,
        },
      );
    } catch (err) {
      console.error('[useTripMapData] subscribeToUserTrips threw:', err);
      setTripLoadDebug((prev) => ({
        ...prev,
        subscriptionError: `${(err as any)?.code || ''} ${(err as any)?.message || String(err)}`.trim(),
        lastUpdatedAt: new Date().toISOString(),
      }));
      setTripsLoading(false);
    }

    return () => {
      cancelled = true;
      clearTimeout(fallbackFetchTimer);
      if (!watchdogCleared) {
        clearTimeout(watchdog);
      }
      unsubscribe();
    };
  }, [firebaseUser?.uid, tripsReloadNonce]);

  const retryLoadTrips = useCallback(() => {
    // Force a resubscribe to Firestore trips stream.
    setTripsLoading(true);
    setTripsReloadNonce((n) => n + 1);
  }, []);

  // Auto-select default trip when none selected
  useEffect(() => {
    if (controlledTripId) {
      return;
    }

    const currentSelectionExists = selectedTripId
      ? tripsForSelection.some((trip) => trip.id === selectedTripId)
      : false;

    if (currentSelectionExists) {
      return;
    }

    const fallbackTripId = getDefaultTripId(tripsForSelection, firebaseUser?.uid);
    if (fallbackTripId) {
      setSelectedTripId(fallbackTripId);
    } else if (selectedTripId !== null) {
      setSelectedTripId(null);
    }
  }, [controlledTripId, selectedTripId, tripsForSelection, firebaseUser?.uid, setSelectedTripId]);

  // Subscribe to itinerary items for selected trip
  useEffect(() => {
    if (!selectedTripId) {
      setItineraryItems([]);
      setItineraryItemsLoading(false);
      itineraryItemsFirstCallbackRef.current = false;
      itineraryEmptySinceRef.current = null;
      hasSeenNonEmptyItineraryRef.current = false;
      itineraryOnlineEmptyVerifyInFlightRef.current = false;
      return;
    }

    let cancelled = false;
    const startedAt = Date.now();

    const cached = itineraryMemoryCache.get(selectedTripId);
    if (cached) {
      setItineraryItems(cached.items);
      setItineraryItemsLoading(false);
      itineraryItemsFirstCallbackRef.current = true;
      hasSeenNonEmptyItineraryRef.current = cached.items.length > 0;
      itineraryEmptySinceRef.current = cached.items.length > 0 ? null : itineraryEmptySinceRef.current;
    } else {
      setItineraryItemsLoading(true);
      itineraryItemsFirstCallbackRef.current = false;
      itineraryEmptySinceRef.current = null;
    }

    const maxSubscriptionRetries = 2;
    const retryDelaysMs = [4000, 8000];
    let subscriptionAttempt = 0;
    let unsubscribe = () => {};
    let subscriptionWatchdog: ReturnType<typeof setTimeout> | null = null;
    let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;

    const clearSubscriptionTimers = () => {
      if (subscriptionWatchdog) {
        clearTimeout(subscriptionWatchdog);
        subscriptionWatchdog = null;
      }
      if (resubscribeTimer) {
        clearTimeout(resubscribeTimer);
        resubscribeTimer = null;
      }
    };

    const startSubscription = () => {
      if (cancelled) return;
      subscriptionAttempt += 1;
      try {
        unsubscribe();
      } catch {
        // ignore
      }

      if (__DEV__) {
        console.warn(`[useTripMapData] Itinerary subscribe attempt ${subscriptionAttempt}`);
      }

      unsubscribe = subscribeToTripItineraryItems(selectedTripId, (items) => {
      // CRITICAL: Only update state if items actually changed to prevent infinite effect loops
      // Compare by serializing IDs and update timestamps to detect real changes
      setItineraryItems((prevItems) => {
        const isOfflineFallback = networkMonitor.shouldUseOfflineDataFallback();
        const isOnlineTransientEmpty =
          !isOfflineFallback &&
          items.length === 0 &&
          prevItems.length > 0 &&
          hasSeenNonEmptyItineraryRef.current;
        if (items.length > 0) {
          hasSeenNonEmptyItineraryRef.current = true;
          itineraryEmptySinceRef.current = null;
          itineraryOnlineEmptyVerifyInFlightRef.current = false;
        } else if (isOnlineTransientEmpty) {
          const now = Date.now();
          if (itineraryEmptySinceRef.current == null) {
            itineraryEmptySinceRef.current = now;
          }
          const emptyForMs = now - itineraryEmptySinceRef.current;
          if (emptyForMs < ITINERARY_EMPTY_SNAPSHOT_GRACE_MS) {
            return prevItems;
          }
          if (!itineraryOnlineEmptyVerifyInFlightRef.current) {
            itineraryOnlineEmptyVerifyInFlightRef.current = true;
            void fetchTripItineraryItems(selectedTripId)
              .then((verifiedItems) => {
                if (!Array.isArray(verifiedItems) || verifiedItems.length === 0) return;
                itineraryMemoryCache.set(selectedTripId, { items: verifiedItems, updatedAt: Date.now() });
                hasSeenNonEmptyItineraryRef.current = true;
                itineraryEmptySinceRef.current = null;
                setItineraryItems((current) => {
                  if (current.length !== verifiedItems.length) return verifiedItems;
                  const currentKey = current.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
                  const nextKey = verifiedItems.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
                  return currentKey === nextKey ? current : verifiedItems;
                });
              })
              .finally(() => {
                itineraryOnlineEmptyVerifyInFlightRef.current = false;
              });
          }
          return prevItems;
        }
        let next: ItineraryItem[];
        if (
          isOfflineFallback &&
          items.length === 0 &&
          prevItems.length > 0
        ) {
          next = prevItems;
        } else if (prevItems.length !== items.length) {
          next = items;
        } else {
          const prevKey = prevItems.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
          const newKey = items.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
          next = prevKey === newKey ? prevItems : items;
        }
        itineraryMemoryCache.set(selectedTripId, { items: next, updatedAt: Date.now() });
        return next;
      });

      itineraryItemsFirstCallbackRef.current = true;
      setItineraryItemsLoading((prev) => (prev === false ? prev : false));
      clearSubscriptionTimers();

      if (__DEV__) {
        const ms = Date.now() - startedAt;
        // Only log slow initial loads to keep the console usable.
        if (ms > 1200) {
          console.log(`[useTripMapData] Itinerary snapshot ready in ${ms}ms (items: ${items.length})`);
        }
      }
      });

      if (subscriptionWatchdog) {
        clearTimeout(subscriptionWatchdog);
      }
      subscriptionWatchdog = setTimeout(() => {
        if (cancelled) return;
        if (itineraryItemsFirstCallbackRef.current) return;
        if (subscriptionAttempt > maxSubscriptionRetries) {
          itineraryItemsFirstCallbackRef.current = true;
          setItineraryItemsLoading(false);
          if (__DEV__) {
            console.warn('[useTripMapData] Itinerary subscription watchdog exhausted; unblocking UI');
          }
          return;
        }

        const retryDelay = retryDelaysMs[Math.min(subscriptionAttempt - 1, retryDelaysMs.length - 1)];
        if (__DEV__) {
          console.warn('[useTripMapData] Itinerary subscription watchdog resubscribing', {
            attempt: subscriptionAttempt + 1,
            retryDelay,
          });
        }
        if (resubscribeTimer) {
          clearTimeout(resubscribeTimer);
        }
        resubscribeTimer = setTimeout(() => {
          startSubscription();
        }, retryDelay);
      }, 8000);
    };

    startSubscription();

    void getCachedTripData(selectedTripId).then((pack) => {
      if (cancelled || !pack?.itinerary?.length) return;
      const fromCache = itineraryItemsFromOfflineCache(pack.itinerary as unknown[], selectedTripId);
      if (fromCache.length === 0) return;
      setItineraryItems((prev) => {
        if (!networkMonitor.shouldUseOfflineDataFallback() && prev.length > 0) return prev;
        if (networkMonitor.shouldUseOfflineDataFallback()) {
          const byId = new Map<string, ItineraryItem>();
          for (const x of prev) byId.set(x.id, x);
          for (const x of fromCache) byId.set(x.id, x);
          const merged = Array.from(byId.values()).sort(
            (a, b) => a.startDate.getTime() - b.startDate.getTime(),
          );
          itineraryMemoryCache.set(selectedTripId, { items: merged, updatedAt: Date.now() });
          return merged;
        }
        itineraryMemoryCache.set(selectedTripId, { items: fromCache, updatedAt: Date.now() });
        return fromCache;
      });
      setItineraryItemsLoading(false);
      itineraryItemsFirstCallbackRef.current = true;
      if (fromCache.length > 0) {
        hasSeenNonEmptyItineraryRef.current = true;
        itineraryEmptySinceRef.current = null;
      }
    });

    // Fallback: if Firestore subscription callback is delayed, do a one-time fetch so UI can proceed.
    // We keep the live subscription, so data stays fresh when it finally arrives.
    const fallbackTimer = setTimeout(() => {
      if (cancelled) return;
      if (itineraryItemsFirstCallbackRef.current) return;

      const fallbackTimeoutMs = 12_000;
      const fallbackFetch = Promise.race([
        fetchTripItineraryItems(selectedTripId),
        new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), fallbackTimeoutMs);
        }),
      ]);

      fallbackFetch
        .then((items) => {
          if (cancelled) return;
          if (!items) {
            itineraryItemsFirstCallbackRef.current = true;
            setItineraryItemsLoading(false);
            if (__DEV__) {
              const ms = Date.now() - startedAt;
              console.warn(`[useTripMapData] Itinerary fallback fetch timed out after ${ms}ms`);
            }
            return;
          }
          setItineraryItems((prevItems) => {
            const isOfflineFallback = networkMonitor.shouldUseOfflineDataFallback();
            if (
              !isOfflineFallback &&
              items.length === 0 &&
              prevItems.length > 0 &&
              hasSeenNonEmptyItineraryRef.current
            ) {
              return prevItems;
            }
            let next: ItineraryItem[];
            if (
              isOfflineFallback &&
              items.length === 0 &&
              prevItems.length > 0
            ) {
              next = prevItems;
            } else if (prevItems.length !== items.length) {
              next = items;
            } else {
              const prevKey = prevItems.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
              const newKey = items.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
              next = prevKey === newKey ? prevItems : items;
            }
            itineraryMemoryCache.set(selectedTripId, { items: next, updatedAt: Date.now() });
            if (next.length > 0) {
              hasSeenNonEmptyItineraryRef.current = true;
              itineraryEmptySinceRef.current = null;
            }
            return next;
          });
          
          itineraryItemsFirstCallbackRef.current = true;
          setItineraryItemsLoading(false);

          if (__DEV__) {
            const ms = Date.now() - startedAt;
            console.warn(`[useTripMapData] Used itinerary fallback fetch after ${ms}ms (items: ${items.length})`);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          // Don't hard-fail the screen; just stop blocking UI.
          itineraryItemsFirstCallbackRef.current = true;
          setItineraryItemsLoading(false);
          if (__DEV__) {
            console.warn('[useTripMapData] Itinerary fallback fetch failed', err);
          }
        });
    }, 1200);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      clearSubscriptionTimers();
      try {
        unsubscribe();
      } catch {
        // ignore
      }
    };
  }, [selectedTripId]);

  // Track current location - non-blocking, don't wait for it.
  // IMPORTANT: keep this subscription stable. Tying it to loading flags causes frequent
  // CLLocationManager teardown/recreate cycles on iOS, which can stall tab transitions.
  useEffect(() => {
    if (!enableLocation) {
      setCurrentLocation(null);
      return;
    }

    let unsubscribeLocation: (() => void) | null = null;
    let initialFixFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    // Use a conservative steady profile instead of dynamically flipping between modes.
    const locationWatchOptions = { timeInterval: 10000, distanceInterval: 25 };

    const setupLocation = async () => {
      // Don't block - get location in background
      // On web, this often fails, so don't wait for it
      if (Platform.OS === 'web') {
        // On web, try to get location but don't block
        getCurrentLocation().then((location) => {
          if (location) {
            setCurrentLocation(location);
          }
        }).catch(() => {
          // Silently fail on web - location permissions are handled by browser
        });

        // Try to watch location, but don't block if it fails
        try {
          unsubscribeLocation = watchLocation((updatedLocation) => {
            setCurrentLocation(updatedLocation);
          }, locationWatchOptions);
        } catch (error) {
          // Silently fail on web
        }
      } else {
        // On native, prefer the live watcher first to avoid creating a short-lived
        // one-shot CLLocationManager right before a long-running watch manager.
        let receivedFromWatch = false;
        unsubscribeLocation = watchLocation((updatedLocation) => {
          receivedFromWatch = true;
          setCurrentLocation(updatedLocation);
        }, locationWatchOptions);

        // Fallback: if the watch stream is slow to emit the first fix, do one
        // on-demand read once so map UI doesn't wait indefinitely.
        initialFixFallbackTimer = setTimeout(() => {
          if (receivedFromWatch) return;
          getCurrentLocation()
            .then((location) => {
              if (location) {
                setCurrentLocation(location);
              }
            })
            .catch(() => {
              // best-effort fallback only
            });
        }, 1500);
      }
    };

    // Don't await - let it run in background.
    // DEFER: Wait 2.5s to avoid contending with initial UI mount and Firestore subscription.
    // This helps prevent "Application Not Responding" (ANR) on slower devices/simulators.
    const startTimer = setTimeout(() => {
      console.log('[useTripMapData] 📍 Starting delayed location setup...');
      setupLocation();
    }, 2500);

    return () => {
      clearTimeout(startTimer);
      if (initialFixFallbackTimer) {
        clearTimeout(initialFixFallbackTimer);
      }
      if (unsubscribeLocation) {
        try {
          unsubscribeLocation();
        } catch (error) {
          // Silently ignore cleanup errors - they're often due to location services being unavailable
          if (__DEV__) {
            console.debug('[useTripMapData] Error during location cleanup:', error);
          }
        }
      }
    };
  }, [enableLocation]);

  // Watchdog: never allow "Loading itinerary…" to persist indefinitely.
  // If we have cache and have been loading for 4s, unblock immediately. Otherwise 12s then fetch.
  useEffect(() => {
    if (!selectedTripId || !itineraryItemsLoading) {
      itineraryLoadingStartedAtRef.current = null;
      if (itineraryLoadingWatchdogRef.current) {
        clearTimeout(itineraryLoadingWatchdogRef.current);
        itineraryLoadingWatchdogRef.current = null;
      }
      return;
    }

    if (itineraryLoadingStartedAtRef.current == null) {
      itineraryLoadingStartedAtRef.current = Date.now();
    }

    if (itineraryLoadingWatchdogRef.current) {
      clearTimeout(itineraryLoadingWatchdogRef.current);
    }

    const cacheEscapeMs = 4_000;
    const cacheEscapeTimer = setTimeout(() => {
      if (!itineraryItemsLoading) return;
      const cached = itineraryMemoryCache.get(selectedTripId);
      if (cached) {
        setItineraryItems(cached.items);
        setItineraryItemsLoading(false);
        itineraryItemsFirstCallbackRef.current = true;
        if (__DEV__) {
          console.warn(`[useTripMapData] Itinerary loading unblocked from cache after ${cacheEscapeMs}ms`);
        }
      }
    }, cacheEscapeMs);

    const watchdogMs = 12_000;
    itineraryLoadingWatchdogRef.current = setTimeout(() => {
      if (!itineraryItemsLoading) return;
      itineraryItemsFirstCallbackRef.current = true;
      setItineraryItemsLoading(false);
      if (__DEV__) {
        const startedAt = itineraryLoadingStartedAtRef.current ?? Date.now();
        const ms = Date.now() - startedAt;
        console.warn(`[useTripMapData] Itinerary loading watchdog fired after ${ms}ms`);
      }
      // Best-effort fetch to recover, without blocking UI.
      fetchTripItineraryItems(selectedTripId)
        .then((items) => {
          if (!items || items.length === 0) return;
          itineraryMemoryCache.set(selectedTripId, { items, updatedAt: Date.now() });
          setItineraryItems((prevItems) => {
            if (prevItems.length !== items.length) return items;
            const prevKey = prevItems.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
            const newKey = items.map((i) => `${i.id}:${i.updatedAt || ''}`).join(',');
            return prevKey === newKey ? prevItems : items;
          });
        })
        .catch(() => {
          // ignore; we already unblocked UI
        });
    }, watchdogMs);

    return () => {
      clearTimeout(cacheEscapeTimer);
      if (itineraryLoadingWatchdogRef.current) {
        clearTimeout(itineraryLoadingWatchdogRef.current);
        itineraryLoadingWatchdogRef.current = null;
      }
    };
  }, [itineraryItemsLoading, selectedTripId]);

  // Build itinerary pins with optimized batch geocoding
  useEffect(() => {
    // Skip geocoding while trips are still loading to prevent effect cascades
    if (tripsLoading) {
      // Clear any partial pin-loading state so we don't get stuck in a loading loop
      // if trips loading toggles during a geocoding run.
      setIsGeocoding(false);
      setIsGeocodingPaused(false);
      setPinsProgress({ total: 0, processed: 0 });
      geocodeRunKeyRef.current = null;
      geocodeRunStartedAtRef.current = null;
      return;
    }

    let isMounted = true;
    let cancelled = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const waitForAppActive = async (): Promise<boolean> => {
      if (AppState.currentState === 'active') return true;

      return await new Promise<boolean>((resolve) => {
        let settled = false;
        const cleanup = () => {
          if (settled) return;
          settled = true;
          try {
            sub.remove();
          } catch {
            // ignore
          }
          clearInterval(cancelPoll);
        };

        const sub = AppState.addEventListener('change', (next) => {
          if (next === 'active') {
            cleanup();
            resolve(true);
          }
        });

        // If the effect is cleaned up while we wait, stop waiting.
        const cancelPoll = setInterval(() => {
          if (cancelled || !isMounted) {
            cleanup();
            resolve(false);
          }
        }, 200);
      });
    };

    const createItineraryPins = async () => {
      // Latest snapshot: effect deps are stable keys, so this async path must not use a stale
      // `itineraryItems` / `tripDestinations` closure (e.g. Firestore hydrating during debounce
      // or a long geocode run).
      const itineraryItems = itineraryItemsRef.current;
      const tripDestinations = tripDestinationsRef.current;
      const destinationCenter = destinationCenterRef.current;
      const selectedCityDestination = tripDestinations[selectedCityIndex] ?? null;
      let sawAbort = false;
      let sawTimeout = false;
      const skipStoredCoordFastPath = forceFreshItineraryGeocodeRef.current;
      forceFreshItineraryGeocodeRef.current = false;

      try {
        setGeocodeRateLimited(false);

        // Restore persisted pins for this trip so "return to app" shows pins immediately (avoids rate-limit burst)
        if (!skipStoredCoordFastPath && selectedTrip?.id && isMounted && !cancelled) {
          try {
            const persisted = await getPersistedMapPins(selectedTrip.id);
            if (persisted?.length) {
              const anchorCoords = resolveTripMapAnchorCoords({
                destinationCenter: destinationCenterRef.current,
                trip: selectedTripRef.current,
                tripDestinations: tripDestinationsRef.current,
                itineraryItems: itineraryItemsRef.current,
              });

              const restoredRaw: MapPin[] = persisted.map((p) => ({
                ...p,
                data: undefined,
              })) as MapPin[];

              const restored =
                anchorCoords == null
                  ? restoredRaw
                  : restoredRaw.filter((p) => {
                      if (p.type !== 'itinerary') return true;
                      const km = haversineDistance(
                        { lat: p.coordinates.latitude, lng: p.coordinates.longitude },
                        { lat: anchorCoords.latitude, lng: anchorCoords.longitude },
                      );
                      if (km > ITINERARY_PIN_MAX_KM_FROM_TRIP_ANCHOR) {
                        if (__DEV__) {
                          console.warn(
                            `[TripMap] Skipping persisted itinerary pin "${p.title}" (${Math.round(km)}km from trip anchor; likely stale).`,
                          );
                        }
                        return false;
                      }
                      return true;
                    });

              if (restored.length) {
                setMapPins((prev) => {
                  const others = prev.filter((pin) => pin.type !== 'itinerary');
                  return [...others, ...restored];
                });
                if (__DEV__) {
                  console.log(
                    '[TripMap] Restored',
                    restored.length,
                    'pins from persistence for trip',
                    selectedTrip.id,
                  );
                }
              }
            }
          } catch (e) {
            if (__DEV__) console.warn('[TripMap] Restore persisted pins failed', e);
          }
        }

        const circuitNow = Date.now();
        if (geocodeCircuitOpenUntilRef.current && circuitNow < geocodeCircuitOpenUntilRef.current) {
          if (__DEV__) {
            const remainingMs = geocodeCircuitOpenUntilRef.current - circuitNow;
            console.warn('[TripMap] Geocode circuit open - skipping run', { remainingMs });
          }
          logIosGeocode('geocode-run-skip', {
            reason: 'circuit-open',
            remainingMs: Math.max(geocodeCircuitOpenUntilRef.current - circuitNow, 0),
            appState: appStateRef.current,
          });
          setIsGeocoding(false);
          setIsGeocodingPaused(false);
          setPinsProgress({ total: 0, processed: 0 });
          return;
        }

        // Only wait for app active when truly backgrounded; when inactive (e.g. overlay) don't block forever.
        if (AppState.currentState === 'background') {
          logIosGeocode('geocode-run-wait-active', { appState: 'background' });
          if (!cancelled && isMounted) setIsGeocodingPaused(true);
          const ok = await waitForAppActive();
          if (!cancelled && isMounted) setIsGeocodingPaused(false);
          if (!ok || cancelled || !isMounted) return;
        } else if (AppState.currentState === 'inactive') {
          // e.g. control center / overlay – wait up to 2s then run so we don't block until app switch
          const ok = await Promise.race([
            waitForAppActive(),
            new Promise<boolean>((r) => setTimeout(() => r(true), 2000)),
          ]);
          if (!ok || cancelled || !isMounted) return;
        }

        // Cancel any prior in-flight requests for the previous run.
        try {
          geocodeAbortRef.current?.abort();
        } catch {
          // ignore
        }
        geocodeAbortRef.current = new AbortController();

        // IMPORTANT: do NOT clear existing itinerary pins immediately.
        // While a refresh/re-geocode is running, clearing pins causes the UI to flicker
        // to "pins to render: 1" (user-location only) and makes the map feel broken.

        const isObviousNonPlaceTitle = (title: string): boolean => {
          const t = title.trim().toLowerCase();
          if (t.length < 4) return true;
          if (/^day\s*\d+$/i.test(t)) return true;
          if (/\b(free time|flexible|tbd|hotel check|check-?in|check-?out|travel day|rest day|departure|arrival)\b/i.test(t)) {
            return true;
          }
          const mealOnly = new Set([
            'lunch',
            'dinner',
            'breakfast',
            'brunch',
            'coffee',
            'tea',
            'snack',
            'drinks',
          ]);
          if (t.length <= 24 && mealOnly.has(t)) return true;
          return false;
        };

        // Geocode address/location when present; otherwise fall back to activity title (real venues often have no address).
        const hasGeocodeSource = (item: ItineraryItem) => {
          const loc = (item.address || item.location || '').trim();
          if (loc.length > 0) return true;
          const title = (item.title || '').trim();
          return title.length > 0 && !isObviousNonPlaceTitle(title);
        };

        const allGeocodableItems = itineraryItems.filter(hasGeocodeSource);
        const scopedDayKey = geocodeScopeDayKeyRef.current;
        // Consume scoped refresh target; subsequent automatic runs should not stay scoped.
        geocodeScopeDayKeyRef.current = null;
        let itemsToGeocode = scopedDayKey
          ? allGeocodableItems.filter((item) => toDateKey(item.startDate) === scopedDayKey)
          : allGeocodableItems;
        if (scopedDayKey && itemsToGeocode.length === 0 && allGeocodableItems.length > 0) {
          if (__DEV__) {
            console.warn(
              '[TripMap] Scoped re-pin matched no items for day',
              scopedDayKey,
              '- geocoding full trip',
            );
          }
          itemsToGeocode = allGeocodableItems;
        }
        // Keep a live set of itinerary pin IDs that should exist for this run.
        // This lets us prune stale pins for deleted items (or items whose location was removed).
        // For day-scoped refreshes, preserve full-trip pins (don't prune non-selected-day pins).
        const activeItineraryPinIds = new Set(
          (scopedDayKey ? allGeocodableItems : itemsToGeocode).map((item) => `itinerary-${item.id}`),
        );
        let createdAnyPins = false;

        const priorityDayKey = geocodePriorityDayKeyRef.current;
        logIosGeocode('geocode-run-init', {
          tripId: selectedTrip?.id ?? null,
          itemsTotal: itineraryItems.length,
          itemsToGeocode: itemsToGeocode.length,
          scopedDayKey: scopedDayKey || null,
          destinations: tripDestinations.length,
          priorityDayKey: priorityDayKey || null,
          appState: appStateRef.current,
        });
        const destinationsKey =
          lastDestinationsKeyRef.current ??
          tripDestinations
            .map((dest) => {
              const city = (dest as any)?.city || '';
              const country = (dest as any)?.country || '';
              const startDate = (dest as any)?.startDate || '';
              const endDate = (dest as any)?.endDate || '';
              return `${city}|${country}|${startDate}|${endDate}`;
            })
            .join(',');
        // Intentionally omit `updatedAt`: persisting resolved coordinates bumps it and would
        // otherwise invalidate the completed-key check and re-run full geocoding (visible as
        // repeated "Pinning locations…"). Location-changing edits are still detected via
        // `buildItineraryGeocodeQueryText` (structured address alone; short/PO lines + city hint / title).
        const pruneAnchor = resolveTripMapAnchorCoords({
          destinationCenter: destinationCenterRef.current,
          trip: selectedTripRef.current,
          tripDestinations: tripDestinationsRef.current,
          itineraryItems: itineraryItemsRef.current,
        });

        const geocodeKey = [
          ITINERARY_GEOCODE_PIPELINE_REVISION,
          selectedTrip?.id || 'no-trip',
          priorityDayKey || 'no-priority',
          destinationsKey || 'no-destinations',
          itemsToGeocode.map((item) => `${item.id}:${buildItineraryGeocodeQueryText(item)}`).join('|'),
        ].join('|');

        if (geocodeCompletedKeyRef.current === geocodeKey) {
          if (__DEV__) {
            console.log('[TripMap] Skipping geocode: inputs unchanged and already completed');
          }
          logIosGeocode('geocode-run-skip', {
            reason: 'already-complete',
            geocodeKey,
          });
          if (pruneAnchor && !cancelled && isMounted) {
            setMapPins((prev) =>
              prev.filter((p) => {
                if (p.type !== 'itinerary') return true;
                const km = haversineDistance(
                  { lat: p.coordinates.latitude, lng: p.coordinates.longitude },
                  { lat: pruneAnchor.latitude, lng: pruneAnchor.longitude },
                );
                if (km > ITINERARY_PIN_MAX_KM_FROM_TRIP_ANCHOR) {
                  if (__DEV__) {
                    console.warn(
                      `[TripMap] Pruning itinerary pin "${p.title}" (${Math.round(km)}km from trip anchor; stale vs completed geocode).`,
                    );
                  }
                  return false;
                }
                return true;
              }),
            );
          }
          return;
        }

        if (geocodeRunKeyRef.current === geocodeKey) {
          if (__DEV__) {
            console.log('[TripMap] Skipping duplicate geocode run for same inputs');
          }
          logIosGeocode('geocode-run-skip', {
            reason: 'duplicate-run',
            geocodeKey,
          });
          return;
        }

        const normalizeLocationKey = (value: string) =>
          value
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/\s*\(\s*/g, ' (')
            .replace(/\s*\)\s*/g, ')');

        // Group by normalized location text to dedupe geocoding work.
        const grouped = new Map<string, ItineraryItem[]>();
        for (const item of itemsToGeocode) {
          const raw = buildItineraryGeocodeQueryText(item);
          const key = normalizeLocationKey(raw);
          const list = grouped.get(key) ?? [];
          list.push(item);
          grouped.set(key, list);
        }

        geocodeRunKeyRef.current = geocodeKey;
        geocodeRunStartedAtRef.current = Date.now();
        logIosGeocode('geocode-run-start', {
          totalItems: itemsToGeocode.length,
          groups: grouped?.size ?? 0,
          priorityDayKey: priorityDayKey || null,
          appState: appStateRef.current,
        });

        if (itemsToGeocode.length === 0) {
          // If there are itinerary items but none have a geocodable location,
          // clear existing itinerary pins so deleted/edited items do not linger on the map.
          if (!cancelled && isMounted) {
            setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'itinerary'));
          }
          setIsGeocoding(false);
          if (!cancelled && isMounted) setPinsProgress({ total: 0, processed: 0 });
          geocodeCompletedKeyRef.current = geocodeKey;
          geocodeRunKeyRef.current = null;
          geocodeRunStartedAtRef.current = null;
          logIosGeocode('geocode-run-skip', {
            reason: 'no-items',
            geocodeKey,
          });
          if (__DEV__) {
            console.log('[TripMap] No itinerary items to geocode');
          }
          return;
        }

        // Early exit if cancelled
        if (cancelled || !isMounted) return;

        if (__DEV__) {
          console.log(`[TripMap] Creating pins for ${itemsToGeocode.length} itinerary items`);
        }

        setIsGeocoding(true);

        // Prioritize a selected day first (if provided via refreshPins).
        const isPriority = (items: ItineraryItem[]) => {
          if (!priorityDayKey) return false;
          return items.some((it) => {
            const k = toDateKey(it.startDate || selectedTrip?.startDate || new Date());
            return !!k && k === priorityDayKey;
          });
        };

        const prioritizedGroups: Array<{ key: string; items: ItineraryItem[] }> = [];
        const otherGroups: Array<{ key: string; items: ItineraryItem[] }> = [];
        for (const [key, items] of grouped.entries()) {
          (isPriority(items) ? prioritizedGroups : otherGroups).push({ key, items });
        }

        // Stable-ish order: earlier activities first to improve perceived usefulness.
        const byStart = (a: { items: ItineraryItem[] }, b: { items: ItineraryItem[] }) => {
          const aT = new Date(a.items[0]?.startDate || 0).getTime();
          const bT = new Date(b.items[0]?.startDate || 0).getTime();
          return aT - bT;
        };
        prioritizedGroups.sort(byStart);
        otherGroups.sort(byStart);

        const groupsToGeocode = [...prioritizedGroups, ...otherGroups];

        persistedMapCoordsItemIdsRef.current.clear();
        pendingCoordPersistsRef.current = [];

        if (!cancelled && isMounted) {
          setPinsProgress({ total: itemsToGeocode.length, processed: 0 });
        }

        const withTimeout = async <T,>(
          promise: Promise<T>,
          ms: number,
          onTimeout?: () => void,
        ): Promise<T | null> => {
          let timeout: ReturnType<typeof setTimeout> | null = null;
          try {
            return await Promise.race([
              promise,
              new Promise<null>((resolve) => {
                timeout = setTimeout(() => {
                  if (onTimeout) onTimeout();
                  resolve(null);
                }, ms);
              }),
            ]);
          } finally {
            if (timeout) clearTimeout(timeout);
          }
        };

        const buildPinFromItem = (item: ItineraryItem, coordinates: LocationCoordinates, cityIndex: number | null): MapPin => ({
          id: `itinerary-${item.id}`,
          // Use selectedTripId (authoritative state) — selectedTrip is derived and can be
          // momentarily null while trips[] is loading, which would leave pins with no tripId
          // and get them filtered out by downstream trip-scope checks.
          tripId: selectedTripId ?? selectedTrip?.id ?? null,
          coordinates,
          title: item.title,
          type: 'itinerary' as const,
          category: item.category,
          data: item,
          dateKey: toDateKey(item.startDate),
          cityIndex,
          locationText: (item.address || item.location || item.title || '').trim() || null,
        });

        const itemHasPersistableCoordinates = (it: ItineraryItem): boolean => {
          const sc = it.coordinates as { latitude?: number; longitude?: number } | undefined;
          if (sc?.latitude == null || sc?.longitude == null) return true;
          const { latitude, longitude } = sc;
          if (
            isNaN(latitude) ||
            isNaN(longitude) ||
            latitude < -90 ||
            latitude > 90 ||
            longitude < -180 ||
            longitude > 180 ||
            (latitude === 0 && longitude === 0) ||
            (Math.abs(latitude - 20) < 1 && Math.abs(longitude) < 1)
          ) {
            return true;
          }
          return false;
        };

        const queuePersistResolvedCoordinates = (its: ItineraryItem[], coords: LocationCoordinates) => {
          const eligible = its.filter((it) => {
            if (persistedMapCoordsItemIdsRef.current.has(it.id)) return false;
            // Manual Re-pin / force-fresh: overwrite wrong-but-"valid" stored coords (otherwise the next
            // open reuses Firestore via the fast-path and pins jump back to Berkane-style drift).
            if (skipStoredCoordFastPath) return true;
            return itemHasPersistableCoordinates(it);
          });
          if (eligible.length === 0) return;
          eligible.forEach((it) => persistedMapCoordsItemIdsRef.current.add(it.id));
          pendingCoordPersistsRef.current.push({ items: eligible, coords });
        };

        // Concurrency-limited queue; lower concurrency + spacing to avoid Cloud Function rate limit (429).
        const CONCURRENCY = 1;
        const THROTTLE_MS = 1_200; // Aggressive cost mode: space requests to reduce burst spend
        let nextGroupIndex = 0;
        let lastGroupStartTime = 0;

        let bufferedPins: MapPin[] = [];
        let flushTimer: ReturnType<typeof setTimeout> | null = null;
        const flushPins = () => {
          if (cancelled || !isMounted) return;
          if (bufferedPins.length === 0) return;
          const pinsToAdd = bufferedPins;
          bufferedPins = [];

          setMapPins((prevPins) =>
            mergeItineraryPinsForActiveItems(prevPins, pinsToAdd, activeItineraryPinIds),
          );
        };

        const enqueuePins = (pins: MapPin[]) => {
          if (pins.length > 0) {
            createdAnyPins = true;
          }
          bufferedPins.push(...pins);
          if (!flushTimer) {
            flushTimer = setTimeout(() => {
              flushTimer = null;
              flushPins();
            }, 200);
          }
        };

        const processGroup = async ({ key, items }: { key: string; items: ItineraryItem[] }) => {
            try {
              const representative = items[0];
              const address = buildItineraryGeocodeQueryText(representative);

              if (!address) {
                return null;
              }

              const getAnchorForRepresentative = (): LocationCoordinates | null => {
                const pushUnique = (
                  centers: LocationCoordinates[],
                  next?: LocationCoordinates | null,
                ) => {
                  if (!next) return;
                  const exists = centers.some(
                    (c) =>
                      Math.abs(c.latitude - next.latitude) < 0.0001 &&
                      Math.abs(c.longitude - next.longitude) < 0.0001,
                  );
                  if (!exists) centers.push(next);
                };

                const centers: LocationCoordinates[] = [];

                const fromTripDestination = (
                  d?: Partial<TripDestination> | null,
                ): LocationCoordinates | null => {
                  if (!d) return null;
                  if (d.coordinates?.latitude != null && d.coordinates?.longitude != null) {
                    return {
                      latitude: d.coordinates.latitude,
                      longitude: d.coordinates.longitude,
                    };
                  }
                  const city = `${d.city || ''}`.trim();
                  if (!city) return null;
                  const known = getDestinationCoordinates(city);
                  return known ? { latitude: known.lat, longitude: known.lng } : null;
                };

                // Prefer trip-wide anchors first so per-day destination rows cannot poison validation
                // of stored coordinates (e.g. wrong admin row) before the real trip center applies.
                pushUnique(centers, destinationCenter);

                const tripDc = selectedTrip?.destinationCoordinates;
                if (tripDc?.latitude != null && tripDc?.longitude != null) {
                  pushUnique(centers, { latitude: tripDc.latitude, longitude: tripDc.longitude });
                }

                const tripDest0 = selectedTrip?.destinations?.[0] ?? tripDestinations?.[0];
                pushUnique(centers, fromTripDestination(tripDest0));

                const firstTripCity =
                  inferDestinationLabelFromItinerary(itineraryItems) ||
                  selectedTrip?.name ||
                  selectedTrip?.destinations?.[0]?.city ||
                  tripDestinations?.[0]?.city ||
                  selectedTrip?.destination;
                if (firstTripCity) {
                  const known = getDestinationCoordinates(firstTripCity);
                  if (known) {
                    pushUnique(centers, { latitude: known.lat, longitude: known.lng });
                  }
                }

                if (selectedCityDestination?.city) {
                  const known = getDestinationCoordinates(selectedCityDestination.city);
                  if (known) {
                    pushUnique(centers, { latitude: known.lat, longitude: known.lng });
                  }
                }

                if (typeof representative.cityIndex === 'number') {
                  pushUnique(centers, fromTripDestination(tripDestinations[representative.cityIndex]));
                }

                const byDate = getDestinationForDate(
                  tripDestinations,
                  representative.startDate || selectedTrip?.startDate || new Date(),
                );
                pushUnique(centers, fromTripDestination(byDate?.destination));

                if (centers.length === 0) return null;
                return centers[0];
              };

              const isGenericTransportOrPlaceholderQuery = (value: string): boolean => {
                const t = value.trim().toLowerCase();
                if (!t) return true;
                const generic = new Set([
                  'flight',
                  'flights',
                  'airport',
                  'airline',
                  'train',
                  'bus',
                  'ferry',
                  'transport',
                  'travel',
                  'departure',
                  'arrival',
                  'pickup',
                  'dropoff',
                  'hotel',
                  'accommodation',
                  'check in',
                  'check-in',
                  'check out',
                  'check-out',
                  'tbd',
                ]);
                if (generic.has(t)) return true;
                if (t.length <= 3) return true;
                if (/^day\s*\d+$/i.test(t)) return true;
                return false;
              };

              // FAST PATH 1: Use stored coordinates from itinerary item if available and valid
              // This dramatically improves loading time by skipping geocoding entirely
              const storedCoords = representative.coordinates as { latitude?: number; longitude?: number } | undefined;
              if (!skipStoredCoordFastPath && storedCoords?.latitude != null && storedCoords?.longitude != null) {
                const { latitude, longitude } = storedCoords;
                // Validate coordinates are reasonable and not default/zero values
                if (!isNaN(latitude) && !isNaN(longitude) &&
                    latitude >= -90 && latitude <= 90 &&
                    longitude >= -180 && longitude <= 180 &&
                    !(latitude === 0 && longitude === 0) && // Reject 0,0
                    !(Math.abs(latitude - 20) < 1 && Math.abs(longitude) < 1)) { // Reject default fallback area
                  const anchor = getAnchorForRepresentative();
                  if (anchor) {
                    const kmFromAnchor = haversineDistance(
                      { lat: latitude, lng: longitude },
                      { lat: anchor.latitude, lng: anchor.longitude },
                    );
                    if (kmFromAnchor > ITINERARY_PIN_MAX_KM_FROM_TRIP_ANCHOR) {
                      if (__DEV__) {
                        console.warn(
                          `[TripMap] Ignoring stale stored coordinates for "${representative.title}" ` +
                            `(${Math.round(kmFromAnchor)}km from destination anchor). Re-geocoding.`,
                        );
                      }
                    } else {
                      if (__DEV__) {
                        console.log(`[TripMap] Using stored coordinates for "${representative.title}": ${latitude}, ${longitude}`);
                      }
                      const pins = items.map((it) =>
                        buildPinFromItem(it, { latitude, longitude }, typeof it.cityIndex === 'number' ? it.cityIndex : null),
                      );
                      return pins;
                    }
                  } else if (__DEV__) {
                    console.warn(
                      `[TripMap] No trip destination anchor for "${representative.title}"; ignoring stored coordinates to force re-geocode.`,
                    );
                  }
                }
              }

              // FAST PATH 2: Check if location string contains coordinates (format: "lat, lng" or "lat,lng")
              const coordMatch = address.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
              let useCoordinates = false;
              let coordinates: LocationCoordinates | null = null;

              if (coordMatch) {
                const latitude = parseFloat(coordMatch[1]);
                const longitude = parseFloat(coordMatch[2]);

                // Validate coordinates are reasonable and not default/zero values
                // Reject coordinates that are clearly wrong (0,0 or near default fallback)
                if (!isNaN(latitude) && !isNaN(longitude) &&
                  latitude >= -90 && latitude <= 90 &&
                  longitude >= -180 && longitude <= 180 &&
                  !(latitude === 0 && longitude === 0) && // Reject 0,0
                  !(Math.abs(latitude - 20) < 1 && Math.abs(longitude) < 1)) { // Reject default fallback area
                  coordinates = { latitude, longitude };
                  useCoordinates = true;
                  if (__DEV__) {
                    console.log(`[TripMap] Found coordinates in location: ${latitude}, ${longitude}`);
                  }
                } else {
                  if (__DEV__) {
                    console.log(`[TripMap] Rejecting invalid coordinates: ${latitude}, ${longitude}`);
                  }
                }
              }

              // Use coordinates from address string if found
              if (useCoordinates && coordinates) {
                queuePersistResolvedCoordinates(items, coordinates);
                const pins = items.map((it) =>
                  buildPinFromItem(it, coordinates!, typeof it.cityIndex === 'number' ? it.cityIndex : null),
                );
                return pins;
              }

              // For generic placeholders like "Flight", avoid global geocoding misses.
              // Use destination anchor instead of resolving to unrelated cities/POIs.
              const anchorForGeneric = getAnchorForRepresentative();
              if (isGenericTransportOrPlaceholderQuery(address) && anchorForGeneric) {
                if (__DEV__) {
                  console.log(
                    `[TripMap] Using destination anchor for generic location "${address}" (${representative.title})`,
                    anchorForGeneric,
                  );
                }
                queuePersistResolvedCoordinates(items, anchorForGeneric);
                const pins = items.map((it) =>
                  buildPinFromItem(
                    it,
                    anchorForGeneric,
                    typeof it.cityIndex === 'number' ? it.cityIndex : null,
                  ),
                );
                return pins;
              }

              // Otherwise, geocode the address with destination context.
              const geocodeOptions: { proximity?: LocationCoordinates; country?: string; request?: any } = {};

              const dateForItem = representative.startDate || selectedTrip?.startDate || new Date();
              const dayKeyForItem = toDateKey(dateForItem);
              const candidates = getDestinationsForDate(tripDestinations, dateForItem);
              const rawLocationText = `${representative.address || ''} ${representative.location || ''} ${representative.title || ''}`.toLowerCase();

              // If multiple destinations overlap this calendar day (common on travel days),
              // do NOT pick a single destination upfront and then fall back to its center.
              // Instead, attempt geocoding against each candidate destination context and pick the first valid one.
              const orderCandidates = (): Array<{ destination: TripDestination; index: number }> => {
                if (candidates.length <= 1) return candidates;

                // 1) Prefer a candidate explicitly mentioned in the item's address/location/title.
                const byMention = candidates.find((c) => {
                  const city = (c.destination.city || '').toLowerCase();
                  const country = (c.destination.country || '').toLowerCase();
                  return (city && rawLocationText.includes(city)) || (country && rawLocationText.includes(country));
                });

                const remaining = candidates.filter((c) => c !== byMention);

                // 2) Prefer the destination that *starts* today (arrival city).
                const startsToday = remaining
                  .map((c) => ({ ...c, startKey: toDateKey(c.destination.startDate) }))
                  .sort((a, b) => {
                    const aStarts = !!dayKeyForItem && a.startKey === dayKeyForItem;
                    const bStarts = !!dayKeyForItem && b.startKey === dayKeyForItem;
                    if (aStarts !== bStarts) return aStarts ? -1 : 1;
                    // 3) Otherwise prefer later destination index.
                    return b.index - a.index;
                  })
                  .map(({ startKey, ...rest }) => rest);

                return byMention ? [byMention, ...startsToday] : startsToday;
              };

              let candidateOrder = orderCandidates();

              // Time-based disambiguation for overlap/travel days:
              // If the item has a time and destinations provide boundary times, pick the best candidate first.
              // Also respect an explicit per-item `cityIndex` override when present.
              const timePicked = pickDestinationForItemByTime(representative, candidateOrder);
              if (timePicked) {
                candidateOrder = [timePicked, ...candidateOrder.filter((c) => c.index !== timePicked.index)];
              } else if (typeof representative.cityIndex === 'number') {
                const byOverride = candidateOrder.find((c) => c.index === representative.cityIndex);
                if (byOverride) {
                  candidateOrder = [byOverride, ...candidateOrder.filter((c) => c.index !== byOverride.index)];
                }
              }

              // Fallback single destination if we have no candidates for this day.
              const fallbackDestinationInfo =
                getDestinationForDate(tripDestinations, dateForItem) ||
                // IMPORTANT: Do NOT fall back to the *currently selected city tab* for itinerary items.
                // That biases geocoding and can place "Paris" items in "London" just because the user
                // is viewing London at the moment. If we can't infer a destination for this item,
                // prefer the trip's first destination (stable) rather than UI state.
                (tripDestinations?.[0] ? { destination: tripDestinations[0], index: 0 } : null);

              // We'll compute geocode context per candidate inside the loop below.
              let destCity: string | undefined = undefined;
              let destCountryFromTrip: string | undefined = undefined;
              // IMPORTANT: pick ONE best candidate to avoid exploding geocode attempts on overlap days.
              let chosenDestinationInfo: { destination: TripDestination; index: number } | null =
                candidateOrder[0] ?? fallbackDestinationInfo;

              const buildContextForDestination = (info: { destination: TripDestination; index: number } | null) => {
                const dCity = info?.destination?.city;
                const dCountryFromTrip = info?.destination?.country;

                // Country code (ISO-3166 alpha-2) for Mapbox geocoding `country=` filter.
                const destinationCountryCode =
                  (dCountryFromTrip ? getCountryCodeFromDestination(dCountryFromTrip) : undefined) ||
                  (dCity ? getCountryCodeFromDestination(dCity) : undefined) ||
                  (tripDestinations?.[0]?.city ? getCountryCodeFromDestination(tripDestinations[0].city) : undefined) ||
                  (selectedTrip?.destinations?.[0]?.city ? getCountryCodeFromDestination(selectedTrip.destinations[0].city) : undefined);

                const destinationLabelForItem =
                  (dCountryFromTrip && dCity ? `${dCity}, ${dCountryFromTrip}` : dCity) ||
                  selectedTrip?.destinations?.[0]?.city ||
                  tripDestinations?.[0]?.city ||
                  inferDestinationLabelFromItinerary(itineraryItems) ||
                  selectedTrip?.destination ||
                  selectedTrip?.name;

                const activityCountry = getCountryCodeForActivity(
                  representative.location || representative.address,
                  destinationLabelForItem || '',
                );

                // Country filter: prefer destination country override (multi-city) over parsed activity country.
                const countryCode = destinationCountryCode || activityCountry;

                const destParts = destinationLabelForItem ? destinationLabelForItem.split(',').map((s) => s.trim()) : [];
                const destCityPart = destParts[0] || destinationLabelForItem || '';
                // IMPORTANT: This is for *human-readable* context appended to the search string.
                // Do not fall back to ISO codes like "fr"/"it" here; it hurts geocoding.
                const destCountryPart = destParts[1] || dCountryFromTrip || undefined;

                const fallbackFromLabel = (() => {
                  const rawLabel =
                    destinationLabelForItem ||
                    selectedCityDestination?.city ||
                    selectedTrip?.destinations?.[0]?.city ||
                    inferDestinationLabelFromItinerary(itineraryItems) ||
                    selectedTrip?.destination ||
                    selectedTrip?.name ||
                    '';
                  const normalizedLabel = rawLabel.split('-')[0]?.trim() || rawLabel;
                  const byLabel = normalizedLabel ? getDestinationCoordinates(normalizedLabel) : null;
                  if (byLabel) return { latitude: byLabel.lat, longitude: byLabel.lng };
                  return destinationCenter || null;
                })();

                const destCenter =
                  info?.destination?.coordinates
                    ? {
                      latitude: info.destination.coordinates.latitude,
                      longitude: info.destination.coordinates.longitude,
                    }
                    : (() => {
                      const approx = dCity ? getDestinationCoordinates(dCity) : null;
                      if (approx) return { latitude: approx.lat, longitude: approx.lng };
                      return fallbackFromLabel;
                    })();

                let proximityAnchor: LocationCoordinates | null = destCenter;
                const marrakechOverride = marrakechAreaProximityOverrideForMorocco({
                  countryCode,
                  destCenter:
                    destCenter &&
                    Number.isFinite(destCenter.latitude) &&
                    Number.isFinite(destCenter.longitude)
                      ? destCenter
                      : null,
                  tripName: selectedTrip?.name,
                  destinationLabel: destinationLabelForItem,
                  destinationCity: dCity,
                  address: representative.address,
                  location: representative.location,
                  title: representative.title,
                });
                if (marrakechOverride) {
                  proximityAnchor = marrakechOverride;
                }

                const opts: { proximity?: LocationCoordinates; country?: string } = {};
                if (countryCode) opts.country = countryCode;
                if (coordinates) {
                  opts.proximity = coordinates;
                } else if (proximityAnchor) {
                  opts.proximity = proximityAnchor;
                } else {
                  const approx = dCity ? getDestinationCoordinates(dCity) : null;
                  if (approx) opts.proximity = { latitude: approx.lat, longitude: approx.lng };
                }

                return {
                  destCity: dCity,
                  destCountryFromTrip: dCountryFromTrip,
                  destinationCountryCode,
                  destinationLabelForItem,
                  countryCode,
                  destCityPart,
                  destCountryPart,
                  destCenter: proximityAnchor,
                  geocodeOptions: opts,
                };
              };

              const finalContext = buildContextForDestination(chosenDestinationInfo || fallbackDestinationInfo);
              destCity = finalContext.destCity;
              destCountryFromTrip = finalContext.destCountryFromTrip;

              // DEV-ONLY diagnostics (must not throw; this runs inside worker queue).
              if (__DEV__ && tripDestinations.length > 0 && !finalContext.destCity) {
                console.warn('[TripMap] Missing destination city for item; using fallback context', {
                  itemId: representative?.id,
                  title: representative?.title,
                  startDate: representative?.startDate,
                  dateForItem,
                  tripDestinations: tripDestinations.map((d) => ({
                    city: d.city,
                    country: d.country,
                    startDate: d.startDate,
                    endDate: d.endDate,
                    hasCoords: !!d.coordinates,
                  })),
                });
              }

              const contextualSearch = finalContext.destinationLabelForItem
                ? buildContextualSearchString(
                  address,
                  representative.address,
                  finalContext.destCityPart,
                  finalContext.destCountryPart,
                )
                : address;

              const searchAttempts: string[] = [contextualSearch];
              const csLower = contextualSearch.toLowerCase();
              const cc = finalContext.countryCode?.toLowerCase();
              if (
                cc === 'ma' &&
                contextualSearch.trim().length > 0 &&
                !csLower.includes('morocco') &&
                !csLower.includes('maroc')
              ) {
                searchAttempts.push(`${contextualSearch.trim()}, Morocco`);
              }

              // Keep requests bounded: short timeout for bulk pin loading (balanced for reliability)
              const request = {
                timeoutMs: 8_000, // Allow slower network without frequent aborts
                maxRetries: 0,
                signal: geocodeAbortRef.current?.signal,
              } as const;

              const proximityForClamp =
                finalContext.destCenter ?? finalContext.geocodeOptions.proximity ?? null;
              const baseOpts = {
                ...finalContext.geocodeOptions,
                ...(proximityForClamp
                  ? {
                      proximity: proximityForClamp,
                      maxDistanceFromProximityKm: ITINERARY_PIN_MAX_KM_FROM_TRIP_ANCHOR,
                    }
                  : {}),
                request,
              };

              let result: GeocodeResult | null = null;
              try {
                for (const search of searchAttempts) {
                  result = await withTimeout(
                    geocodeAddress(search, baseOpts),
                    10_000,
                    () => {
                      sawTimeout = true;
                    },
                  );
                  if (result) break;
                }
              } catch (geocodeErr) {
                if (geocodeErr instanceof Error && geocodeErr.message === GEOCODE_RATE_LIMITED && isMounted) {
                  setGeocodeRateLimited(true);
                }
                return null;
              }

              // Check if cancelled before proceeding
              if (cancelled || !isMounted) {
                return null;
              }

              // If we still don't have valid coordinates, skip this item
              if (!result?.coordinates?.length) {
                if (__DEV__) {
                  console.warn(`[TripMap] Could not geocode address for "${representative.title}": ${address}`);
                }
                return null;
              }

              const [lng, lat] = result.coordinates;
              const coords = { latitude: lat, longitude: lng };
              const resolvedCityIndex = (typeof representative.cityIndex === 'number' ? representative.cityIndex : null) ??
                (chosenDestinationInfo ? chosenDestinationInfo.index : null);

              const pins = items.map((it) =>
                buildPinFromItem(
                  it,
                  coords,
                  (typeof it.cityIndex === 'number' ? it.cityIndex : null) ?? resolvedCityIndex,
                ),
              );

              queuePersistResolvedCoordinates(items, coords);

              return pins;
            } catch (error: any) {
              const code = error?.code || error?.name;
              if (code === 'EABORT' || code === 'AbortError') {
                sawAbort = true;
              } else if (code === 'ETIMEDOUT' || `${error?.message || ''}`.toLowerCase().includes('timed out')) {
                sawTimeout = true;
              }
              console.error(`[TripMap] Error geocoding group:`, { key }, error);
              return null;
            } finally {
              // Update progress even if a group couldn't be geocoded (avoid infinite "loading pins")
              if (!cancelled && isMounted) {
                setPinsProgress((prev) => ({
                  total: prev.total,
                  processed: Math.min(prev.total, prev.processed + items.length),
                }));
              }
            }
        };

        const worker = async () => {
          while (!cancelled && isMounted) {
            const idx = nextGroupIndex++;
            if (idx >= groupsToGeocode.length) return;
            if (appStateRef.current !== 'active') {
              if (!cancelled && isMounted) setIsGeocodingPaused(true);
              const ok = await waitForAppActive();
              if (!cancelled && isMounted) setIsGeocodingPaused(false);
              if (!ok || cancelled || !isMounted) return;
            }
            // Throttle: space out Cloud Function calls to avoid rate limit (429)
            const now = Date.now();
            const waitMs = Math.max(0, THROTTLE_MS - (now - lastGroupStartTime));
            if (waitMs > 0) {
              await new Promise((r) => setTimeout(r, waitMs));
            }
            lastGroupStartTime = Date.now();
            const group = groupsToGeocode[idx];
            const pins = await processGroup(group);
            if (cancelled || !isMounted) return;
            if (pins && pins.length > 0) {
              enqueuePins(pins);
            }
          }
        };

        // Add a maximum time limit for the entire geocoding process (30 seconds)
        // This prevents the UI from being stuck if geocoding takes too long
        const maxGeocodeTime = 30_000;
        const geocodeStartTime = Date.now();
        
        const geocodeWithTimeout = Promise.race([
          Promise.all(Array.from({ length: Math.min(CONCURRENCY, groupsToGeocode.length) }, worker)),
          new Promise<void>((resolve) => {
            setTimeout(() => {
              if (__DEV__) {
                console.warn(`[TripMap] Geocoding hit ${maxGeocodeTime}ms time limit, completing with partial results`);
              }
              sawTimeout = true;
              logIosGeocode('geocode-run-time-limit', {
                maxGeocodeTime,
                processed: pinsProgressUpdatedAtRef.current ? pinsProgress : null,
              });
              resolve();
            }, maxGeocodeTime);
          }),
        ]);
        
        await geocodeWithTimeout;

        if (flushTimer) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        flushPins();
        
        if (__DEV__) {
          const elapsed = Date.now() - geocodeStartTime;
          console.log(`[TripMap] Geocoding completed in ${elapsed}ms`);
        }
        logIosGeocode('geocode-run-complete', {
          elapsedMs: Date.now() - geocodeStartTime,
          sawAbort,
          sawTimeout,
          createdAnyPins,
          total: itemsToGeocode.length,
        });

        setIsGeocoding(false);
        
        // Ensure progress shows complete even if some items failed
        if (!cancelled && isMounted) {
          setPinsProgress((prev) => ({ total: prev.total, processed: prev.total }));
        }
        // Single repaint nudge after geocode (extra delayed clones were causing visible pin blink
        // when combined with landmark merges and native annotation remounts).
        if (!cancelled && isMounted) {
          InteractionManager.runAfterInteractions(() => {
            requestAnimationFrame(() => {
              if (!cancelled && isMounted) setMapPins((prev) => (prev.length ? [...prev] : prev));
            });
          });
        }
        
        // Track completion to avoid re-running for identical inputs.
        if ((createdAnyPins || itemsToGeocode.length === 0) && !sawAbort && !sawTimeout) {
          geocodeCompletedKeyRef.current = geocodeKey;
        }
        geocodeRunKeyRef.current = null;
        geocodeRunStartedAtRef.current = null;
        if (!sawAbort && !sawTimeout) {
          geocodeNeedsRetryRef.current = false;
          geocodeFailureCountRef.current = 0;
          geocodeFailureWindowStartedAtRef.current = null;
          geocodeCircuitOpenUntilRef.current = 0;
        }

        // Persist itinerary pins for this trip so "return to app" shows pins without re-geocoding
        if (!cancelled && isMounted && selectedTrip?.id) {
          setTimeout(() => {
            const pins = mapPinsRef.current.filter(
              (p) => p.type === 'itinerary' && (p.tripId === selectedTrip?.id || !p.tripId),
            );
            if (pins.length) {
              setPersistedMapPins(selectedTrip.id, pins);
            }
          }, 600);
        }

        // Persist resolved coordinates to Firestore AFTER the full geocode run completes,
        // so mid-run writes don't trigger the Firestore subscription → effect restart cycle.
        if (!cancelled && isMounted && selectedTrip?.id && firebaseUser?.uid) {
          const tripIdForPersist = selectedTrip.id;
          const pending = [...pendingCoordPersistsRef.current];
          pendingCoordPersistsRef.current = [];
          if (pending.length > 0) {
            const coordByItemId = new Map<string, LocationCoordinates>();
            for (const { items: its, coords: c } of pending) {
              for (const it of its) {
                coordByItemId.set(it.id, c);
              }
            }
            InteractionManager.runAfterInteractions(() => {
              for (const { items: its, coords } of pending) {
                for (const it of its) {
                  void updateItineraryItem(tripIdForPersist, it.id, {
                    coordinates: { latitude: coords.latitude, longitude: coords.longitude },
                  }).catch(() => {
                    persistedMapCoordsItemIdsRef.current.delete(it.id);
                  });
                }
              }
              if (coordByItemId.size > 0) {
                setItineraryItems((prev) =>
                  prev.map((item) => {
                    const c = coordByItemId.get(item.id);
                    return c
                      ? { ...item, coordinates: { latitude: c.latitude, longitude: c.longitude } }
                      : item;
                  }),
                );
              }
            });
          }
        }

        if ((sawAbort || sawTimeout) && !cancelled && isMounted) {
          const progress = pinsProgressRef.current;
          const progressComplete = progress.total > 0 && progress.processed >= progress.total;
          const hasExistingPins = mapPinsRef.current.some((p) => p.type === 'itinerary');
          if (progressComplete && (createdAnyPins || hasExistingPins)) {
            geocodeNeedsRetryRef.current = false;
            logIosGeocode('geocode-retry-skipped', {
              reason: 'progress-complete',
              sawAbort,
              sawTimeout,
              progress,
            });
            return;
          }

          const now = Date.now();
          const windowMs = 2 * 60_000;
          if (
            geocodeFailureWindowStartedAtRef.current == null ||
            now - geocodeFailureWindowStartedAtRef.current > windowMs
          ) {
            geocodeFailureWindowStartedAtRef.current = now;
            geocodeFailureCountRef.current = 0;
          }
          geocodeFailureCountRef.current += 1;

          const maxFailures = 3;
          if (geocodeFailureCountRef.current >= maxFailures) {
            geocodeCircuitOpenUntilRef.current = now + 60_000;
            geocodeNeedsRetryRef.current = false;
            if (__DEV__) {
              console.warn('[TripMap] Geocode circuit opened', {
                failures: geocodeFailureCountRef.current,
                windowMs,
              });
            }
            return;
          }

          geocodeNeedsRetryRef.current = true;
          logIosGeocode('geocode-retry-scheduled', {
            reason: sawAbort ? 'abort' : 'timeout',
            failures: geocodeFailureCountRef.current,
            windowMs,
            progress,
          });
          scheduleGeocodeRetry(sawAbort ? 'abort' : 'timeout');
        }

        // Clear priority day after successful geocoding
        geocodePriorityDayKeyRef.current = null;
      } catch (error) {
        // Top-level catch to prevent geocoding errors from crashing the app
        console.error('[TripMap] Error in createItineraryPins:', error);
        logIosGeocode('geocode-run-error', {
          error: `${(error as Error)?.message || 'unknown'}`,
        });
        setIsGeocoding(false);
        if (!cancelled && isMounted) {
          // Ensure UI isn't stuck in "loading pins" after an unexpected error
          setPinsProgress((prev) => ({ total: prev.total, processed: prev.total }));
        }
        geocodeRunKeyRef.current = null;
        geocodeRunStartedAtRef.current = null;
      }
    };

    // If the itinerary stream momentarily goes empty (common during trip/day switches or cache refresh),
    // don't immediately clear pins — it causes a visible "pins to render: 1" flicker.
    // Instead, debounce clearing; if items arrive shortly after, we cancel the clear.
    if (clearItineraryPinsTimeoutRef.current) {
      clearTimeout(clearItineraryPinsTimeoutRef.current);
      clearItineraryPinsTimeoutRef.current = null;
    }

    if (itineraryItems.length > 0) {
      if (__DEV__) {
        console.log(`[TripMap] Starting geocoding for ${itineraryItems.length} itinerary items (200ms debounce)`);
      }
      // Add 200ms debounce to let state settle before geocoding (reduced from 500ms for faster UX)
      debounceTimer = setTimeout(() => {
        if (!cancelled && isMounted) {
          createItineraryPins();
        }
      }, 200);
    } else {
      if (__DEV__) {
        console.log('[TripMap] No itinerary items to geocode');
      }
      // Don't clear pins while itinerary is still loading (e.g. after cache clear) — items may arrive shortly.
      // Also preserve existing pins while in offline fallback mode, because empty snapshots can be transient.
      if (!itineraryItemsLoading && !networkMonitor.shouldUseOfflineDataFallback()) {
        clearItineraryPinsTimeoutRef.current = setTimeout(() => {
          if (cancelled || !isMounted) return;
          setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'itinerary'));
        }, 800);
      }
      setIsGeocoding(false);
      setPinsProgress({ total: 0, processed: 0 });
    }

    return () => {
      cancelled = true;
      isMounted = false;
      // Abort in-flight geocoding HTTP requests immediately (prevents long stalls on iOS transitions).
      try {
        geocodeAbortRef.current?.abort();
      } catch {
        // ignore
      } finally {
        geocodeAbortRef.current = null;
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (clearItineraryPinsTimeoutRef.current) {
        clearTimeout(clearItineraryPinsTimeoutRef.current);
        clearItineraryPinsTimeoutRef.current = null;
      }
      setIsGeocoding(false);
      setIsGeocodingPaused(false);
      // Avoid leaving a stale "Pinning locations" state if this run is cancelled mid-flight.
      setPinsProgress({ total: 0, processed: 0 });
      geocodeRunKeyRef.current = null;
      geocodeRunStartedAtRef.current = null;
    };
  // NOTE: Do not list raw `itineraryItems` / `tripDestinations` here — new array references from
  // Firestore (e.g. after persisting coordinates) would restart this effect, run cleanup (abort +
  // reset progress), and feel like "repinning" 2–3× even when geocode inputs are unchanged.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryGeocodeEffectKey, tripDestinationsGeocodeKey, tripsLoading, itineraryItemsLoading, selectedTripId, mapGeocodeRefreshNonce]);

  const landmarkFetchCityLabel = useMemo((): string | null => {
    const normalize = (value?: string | null): string | null => {
      const v = `${value || ''}`.trim();
      if (!v) return null;
      // Ignore city codes like TOK/NYC and very short placeholders.
      if (/^[A-Z]{2,4}$/.test(v)) return null;
      if (v.length < 3) return null;
      return v;
    };
    const fromTripName = (() => {
      const raw = `${selectedTrip?.name || ''}`.trim();
      if (!raw) return null;
      // e.g. "Tokyo-Kyoto-Osaka 14-Day Family Adventure" => "Tokyo"
      const firstChunk = raw.split('-')[0]?.trim() || raw;
      const cleaned = firstChunk
        // Remove "7-day", "7 day", "7days", etc and trailing descriptors.
        .replace(/\b\d+\s*[- ]*days?\b.*$/i, '')
        // Remove trailing standalone counts from names like "Italy 7".
        .replace(/\s+\d+\s*$/, '')
        .trim();
      // Trip names with residual digits are usually itinerary titles, not place labels.
      if (/\d/.test(cleaned)) return null;
      return normalize(cleaned);
    })();
    const inferredDestinationLabel = normalize(inferDestinationLabelFromItinerary(itineraryItems));
    const inferredCityLabel = normalize(inferCityForLandmarks(itineraryItems));
    const selectedTripDestination = normalize(selectedTrip?.destination);
    const shouldPreferInferredLabel = (() => {
      // If we have an itinerary-derived city label that maps to the same country,
      // prefer it over broad destination labels like "Italy" to improve landmark recall.
      if (!selectedTripDestination) return false;
      const inferred = inferredDestinationLabel || inferredCityLabel;
      if (!inferred) return false;
      if (selectedTripDestination.toLowerCase() === inferred.toLowerCase()) return false;
      if (selectedTripDestination.includes(',')) return false;
      const destinationCode = getCountryCodeFromDestination(selectedTripDestination);
      const inferredCode = getCountryCodeFromDestination(inferred);
      if (!destinationCode || !inferredCode || destinationCode !== inferredCode) return false;
      return selectedTripDestination.split(/\s+/).length <= 3;
    })();

    // Priority: trip-defined destinations always win over itinerary-derived text.
    // Itinerary prose (e.g. "Ingreso por la frontera") is scraped from free-form fields
    // and can easily produce garbage labels that poison landmark fetches.
    const base =
      normalize(selectedCityDestination?.city) ||
      normalize(tripDestinations?.[0]?.city) ||
      normalize(selectedTrip?.destinations?.[0]?.city) ||
      (shouldPreferInferredLabel ? inferredDestinationLabel || inferredCityLabel : selectedTripDestination) ||
      fromTripName ||
      inferredDestinationLabel ||
      inferredCityLabel ||
      selectedTripDestination ||
      null;

    if (!base) return null;

    return enrichCityLabelWithCountryParts(base, {
      country:
        selectedCityDestination?.country ||
        tripDestinations?.[0]?.country ||
        selectedTrip?.destinations?.[0]?.country,
      fullDestination: selectedTrip?.destination,
    });
  }, [
    selectedCityDestination?.city,
    tripDestinations,
    selectedTrip?.destinations,
    selectedTrip?.destination,
    selectedTrip?.name,
    itineraryItems,
    inferCityForLandmarks,
    inferDestinationLabelFromItinerary,
  ]);

  // Fetch landmarks and trendy places
  useEffect(() => {
    let isMounted = true;
    const fetchGeneration = ++landmarkFetchGenerationRef.current;

    const fetchPlaces = async () => {
      const activeDestinationLabel = landmarkFetchCityLabel;
      if (!activeDestinationLabel) {
        if (isMounted) {
          setRawLandmarks([]);
          setLandmarkSource('none');
          landmarkRefreshPendingRef.current = false;
          setLandmarksRefreshing(false);
        }
        return;
      }

      try {
        const normalizeCountryCode = (value?: string): string | undefined => {
          if (!value) return undefined;
          // Mapbox Search Box `country=` expects ISO 3166-1 alpha-2 codes in lowercase.
          // Uppercase codes are accepted by some endpoints but silently ignored on others.
          const trimmed = value.trim();
          return /^[A-Za-z]{2}$/.test(trimmed) ? trimmed.toLowerCase() : undefined;
        };
        const country =
          normalizeCountryCode(
          (selectedTrip?.destination ? getCountryCodeFromDestination(selectedTrip.destination) : undefined) ||
          (tripDestinations?.[0] && 'country' in tripDestinations[0]
            ? getCountryCodeFromDestination((tripDestinations[0] as any).country)
            : undefined) ||
          (selectedTrip?.destinations?.[0] && 'country' in selectedTrip.destinations[0]
            ? getCountryCodeFromDestination((selectedTrip.destinations[0] as any).country)
            : undefined) ||
          getCountryCodeFromDestination(activeDestinationLabel) ||
          undefined
          );

        // Prefer destination proximity (not device proximity) for landmark search.
        // Using the user's location (often a different country) can bias POI results incorrectly.
        //
        // IMPORTANT: many cities are not in `DESTINATION_COORDINATES`. In that case, geocode the city label
        // once and use it as proximity; otherwise we end up using a country centroid which yields generic results.
        const destinationProximity = await (async (): Promise<LocationCoordinates | null> => {
          // 1) Static mapping for the city label (best).
          const mappedCity = getDestinationCoordinates(activeDestinationLabel);
          if (mappedCity) return { latitude: mappedCity.lat, longitude: mappedCity.lng };

          // 2) Geocoded city label (good).
          const geo = await geocodeAddress(activeDestinationLabel, country ? { country } : undefined).catch(() => null);
          if (geo?.coordinates?.length === 2) {
            return { latitude: geo.coordinates[1], longitude: geo.coordinates[0] };
          }

          // 3) Use current map itinerary pins as anchor when city lookup is weak.
          // (These are often available before itinerary item documents are backfilled.)
          const itineraryPinAnchor = (() => {
            const coords = mapPinsRef.current
              .filter((p) => p.type === 'itinerary')
              .map((p) => p.coordinates)
              .filter((c) =>
                Number.isFinite(c?.latitude) &&
                Number.isFinite(c?.longitude) &&
                Math.abs(c.latitude) <= 90 &&
                Math.abs(c.longitude) <= 180 &&
                !(c.latitude === 0 && c.longitude === 0),
              );
            if (!coords.length) return null;
            const sample = coords.slice(0, 16);
            const sum = sample.reduce(
              (acc, c) => ({ lat: acc.lat + c.latitude, lng: acc.lng + c.longitude }),
              { lat: 0, lng: 0 },
            );
            return {
              latitude: sum.lat / sample.length,
              longitude: sum.lng / sample.length,
            };
          })();
          if (itineraryPinAnchor) return itineraryPinAnchor;

          // 4) Use existing itinerary item coordinates as anchor when city lookup is weak.
          // This keeps landmark discovery in the same metro area the itinerary already pins to.
          const itineraryAnchor = (() => {
            const isFiniteCoord = (lat?: number, lng?: number) =>
              typeof lat === 'number' &&
              typeof lng === 'number' &&
              Number.isFinite(lat) &&
              Number.isFinite(lng) &&
              Math.abs(lat) <= 90 &&
              Math.abs(lng) <= 180 &&
              !(lat === 0 && lng === 0);
            const coords = itineraryItems
              .map((it) => it.coordinates)
              .filter((c) => isFiniteCoord(c?.latitude, c?.longitude)) as Array<{
              latitude: number;
              longitude: number;
            }>;
            if (!coords.length) return null;
            const sample = coords.slice(0, 16);
            const sum = sample.reduce(
              (acc, c) => ({
                lat: acc.lat + c.latitude,
                lng: acc.lng + c.longitude,
              }),
              { lat: 0, lng: 0 },
            );
            return {
              latitude: sum.lat / sample.length,
              longitude: sum.lng / sample.length,
            };
          })();
          if (itineraryAnchor) return itineraryAnchor;

          // 5) Existing destinationCenter (often country centroid). Read ref so map-center updates
          // don't retrigger the whole landmark fetch effect (large trips saw 2–3 extra passes).
          const dc = destinationCenterRef.current;
          if (dc) return dc;

          // 6) Static mapping for trip destination as a last resort.
          const approxLabel = selectedTrip?.destination || activeDestinationLabel;
          const approx = approxLabel ? getDestinationCoordinates(approxLabel) : undefined;
          return approx ? { latitude: approx.lat, longitude: approx.lng } : null;
        })();

        console.warn('[TripMap] Fetching landmarks for:', {
          activeDestinationLabel,
          tripDestination: selectedTrip?.destination,
          tripName: selectedTrip?.name,
          selectedCity: selectedCityDestination?.city,
          country,
          proximity: destinationProximity || null,
        });
        if (isMounted) setLandmarkFetchProximity(destinationProximity || null);
        // Hydrate quickly from local cache first so landmarks appear immediately while live refresh runs.
        const cachedPlaces = await getCachedLandmarks(activeDestinationLabel, { allowExpired: true });
        if (isMounted) {
          if (cachedPlaces.length > 0) {
            setRawLandmarks(cachedPlaces);
            setLandmarkSource(isBaselineSeeded(cachedPlaces) ? 'baseline' : 'cache');
            if (__DEV__) {
              console.warn('[TripMap] Hydrated landmarks from cache:', cachedPlaces.length);
            }
          } else {
            // No cache for the new city label: clear any stale landmarks from a previously
            // viewed city so the user doesn't see foreign pins while the live fetch runs.
            setRawLandmarks([]);
            setLandmarkSource('none');
          }
        }

        const landmarkLiveFetchKey = `${activeDestinationLabel.trim().toLowerCase()}::${country ?? 'none'}`;
        const hasAttemptedLiveFetch = landmarkLiveFetchAttemptedRef.current.has(landmarkLiveFetchKey);
        // Read ref here (not earlier): overlapping effect runs can clear it in another completion's
        // `finally` before we decide — a stale "manual" false would defer live fetch after cache was
        // cleared, so pins stay empty while the UI still shows "refreshing landmarks".
        const shouldDeferLiveFetch =
          !landmarkRefreshPendingRef.current && pinsLoading && itineraryItems.length > 0;

        if (shouldDeferLiveFetch) {
          if (__DEV__) {
            console.warn('[TripMap] Deferring live landmark fetch until itinerary pinning settles', {
              city: activeDestinationLabel,
              pinsLoading,
              itineraryItems: itineraryItems.length,
            });
          }
          return;
        }

        // Spinner only for work that hits the network (manual refresh already set true in
        // `refreshLandmarkSuggestions`). Avoids repeated "Refreshing landmarks" during cache hydrate.
        let landmarkRefreshUiStarted = landmarkRefreshPendingRef.current;
        const ensureLandmarkRefreshUi = () => {
          if (!landmarkRefreshUiStarted && isMounted) {
            landmarkRefreshUiStarted = true;
            setLandmarksRefreshing(true);
          }
        };

        // If we only have manual baseline seeds in cache, keep trying for a real Mapbox-backed
        // batch whenever this effect runs. Otherwise the first session stops at baselines until
        // the user hits manual refresh (attempted ref was set even when the live call failed
        // or returned empty and we fell back to baseline).
        const cachedIsBaselineOnly = isBaselineSeeded(cachedPlaces);
        if (
          !landmarkRefreshPendingRef.current &&
          hasAttemptedLiveFetch &&
          cachedPlaces.length > 0 &&
          !cachedIsBaselineOnly
        ) {
          if (__DEV__) {
            console.warn('[TripMap] Skipping live landmark fetch (already attempted this session)', {
              city: activeDestinationLabel,
              country: country ?? null,
            });
          }
          if (isMounted) {
            setRawLandmarks(cachedPlaces);
            setLandmarkSource(
              cachedPlaces.length > 0
                ? isBaselineSeeded(cachedPlaces)
                  ? 'baseline'
                  : 'cache'
                : 'none',
            );
          }
          return;
        }

        landmarkLiveFetchAttemptedRef.current.add(landmarkLiveFetchKey);

        ensureLandmarkRefreshUi();
        let places = await fetchLandmarks(activeDestinationLabel, {
          limit:
            mode === 'preview'
              ? landmarkDensityPreset.landmarkFetchLimitPreview
              : landmarkDensityPreset.landmarkFetchLimitFull,
          proximity: destinationProximity || undefined,
          country,
          debug: __DEV__,
        });
        // Recovery path: some city+proximity/country combinations return no POIs even though
        // the destination is valid. Retry once per trip/city/session with a relaxed query.
        const relaxedRetryKey = `${selectedTripId ?? 'none'}:${activeDestinationLabel.toLowerCase()}`;
        const allowRelaxedRetry = !landmarkRelaxedFallbackUsedRef.current.has(relaxedRetryKey);
        if (places.length === 0 && allowRelaxedRetry) {
          landmarkRelaxedFallbackUsedRef.current.add(relaxedRetryKey);
          const relaxed = await fetchLandmarks(activeDestinationLabel, {
            limit:
              mode === 'preview'
                ? landmarkDensityPreset.landmarkFetchLimitPreview
                : landmarkDensityPreset.landmarkFetchLimitFull,
            // Drop strict constraints for fallback discovery.
            proximity: undefined,
            country: undefined,
            debug: __DEV__,
          });
          if (relaxed.length > 0) {
            places = relaxed;
            if (__DEV__) {
              console.warn('[TripMap] Landmark fetch recovered via relaxed fallback', {
                activeDestinationLabel,
                recovered: relaxed.length,
              });
            }
          }
        }
        if (isMounted) {
          const nextPlaces = places.length > 0 ? places : cachedPlaces;
          setRawLandmarks(nextPlaces);
          if (places.length > 0) {
            setLandmarkSource(isBaselineSeeded(places) ? 'baseline' : 'live');
          } else if (cachedPlaces.length > 0) {
            setLandmarkSource(isBaselineSeeded(cachedPlaces) ? 'baseline' : 'cache');
          } else {
            setLandmarkSource('none');
          }
          console.warn('[TripMap] Landmarks fetched:', places.length, {
            usingFallbackCache: places.length === 0 && cachedPlaces.length > 0,
            finalCount: nextPlaces.length,
          });
        }
      } catch (error) {
        console.error('[TripMap] Error fetching landmarks:', error);
        // Keep prior suggestions on failure so we don't churn effects / map state (itinerary pins stay stable).
      } finally {
        // Only the latest in-flight fetch may end the refresh UI / manual-refresh latch; otherwise
        // an older completion can clear state while a newer run still holds `landmarksRefreshing`.
        if (landmarkFetchGenerationRef.current === fetchGeneration) {
          landmarkRefreshPendingRef.current = false;
          setLandmarksRefreshing(false);
        }
      }
    };

    fetchPlaces();

    return () => {
      isMounted = false;
    };
  }, [
    landmarkFetchCityLabel,
    landmarksReloadNonce,
    selectedTrip?.destination,
    selectedTrip?.destinations,
    tripDestinations,
    selectedTripId,
    mode,
    landmarkDensityPreset,
    isBaselineSeeded,
  ]);

  // Long itineraries (e.g. Tokyo template ~55 pins) keep `pinsLoading` true for a while and it can
  // flip repeatedly while batches settle. Listing `pinsLoading` on the fetch effect restarted
  // landmark hydration + live fetch on every change, so landmarks "popped in" then appeared to
  // refresh. Bump reload once when pinning finishes instead.
  useEffect(() => {
    if (prevPinsLoadingForLandmarkFetchRef.current === null) {
      prevPinsLoadingForLandmarkFetchRef.current = pinsLoading;
      return;
    }
    const prev = prevPinsLoadingForLandmarkFetchRef.current;
    prevPinsLoadingForLandmarkFetchRef.current = pinsLoading;
    if (
      prev &&
      !pinsLoading &&
      itineraryItems.length > 0 &&
      !landmarkRefreshPendingRef.current
    ) {
      if (landmarkReloadCoalesceTimerRef.current) {
        clearTimeout(landmarkReloadCoalesceTimerRef.current);
      }
      landmarkReloadCoalesceTimerRef.current = setTimeout(() => {
        landmarkReloadCoalesceTimerRef.current = null;
        setLandmarksReloadNonce((n) => n + 1);
      }, 360);
    }
  }, [pinsLoading, itineraryItems.length]);

  const refreshLandmarkSuggestions = useCallback(async () => {
    const city = landmarkFetchCityLabel?.trim();
    if (!city) {
      Alert.alert('No destination', 'Add a destination to your trip to refresh map suggestions.');
      return;
    }
    const cityKeyPrefix = `${city.toLowerCase()}::`;
    // Manual refresh must bypass the per-session auto-fetch guard.
    // A previous in-flight fetch can clear `landmarkRefreshPendingRef` before the next
    // effect run; removing the city key(s) guarantees the live fetch still executes.
    for (const key of Array.from(landmarkLiveFetchAttemptedRef.current)) {
      if (key.startsWith(cityKeyPrefix)) {
        landmarkLiveFetchAttemptedRef.current.delete(key);
      }
    }
    landmarkRefreshPendingRef.current = true;
    setLandmarksRefreshing(true);
    try {
      await clearPlacesCache(city);
      setLandmarksReloadNonce((n) => n + 1);
    } catch (e) {
      console.error('[TripMap] refreshLandmarkSuggestions failed', e);
      landmarkRefreshPendingRef.current = false;
      setLandmarksRefreshing(false);
      Alert.alert('Could not refresh', 'Check your connection and try again.');
    }
  }, [landmarkFetchCityLabel]);

  useEffect(() => {
    const selectedTripNow = selectedTripRef.current;
    const itineraryItemsNow = itineraryItemsRef.current;

    if (!selectedTripNow) {
      setLandmarks([]);
      setLandmarkSource('none');
      setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'landmark'));
      return;
    }

    if (!rawLandmarks.length) {
      setLandmarks([]);
      setLandmarkSource('none');
      setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'landmark'));
      return;
    }

    const enrichedPlaces = annotatePlacesForTrip(rawLandmarks, itineraryItemsNow, selectedTripNow);
    setLandmarks(enrichedPlaces);

    const landmarkPins: MapPin[] = enrichedPlaces.map((place) => ({
      id: `landmark-${place.id}`,
      tripId: selectedTripId ?? selectedTripNow.id ?? null,
      coordinates: place.coordinates,
      title: place.name,
      type: 'landmark',
      data: place,
      subtitle: place.trendingReason || place.address,
      highlight: place.isMustSee
        ? 'must_see'
        : place.tags?.includes('trending')
          ? 'trending'
          : undefined,
      badges: place.isMustSee
        ? ['Must-see']
        : place.tags?.includes('top-rated')
          ? ['Top rated']
          : undefined,
    }));

    setMapPins((prevPins) => {
      // Never drop itinerary / user / search pins when merging discovery landmarks (avoids races with geocode updates).
      const userSearch = prevPins.filter((p) => p.type === 'user' || p.type === 'search');
      const itineraryPins = prevPins.filter((p) => p.type === 'itinerary');
      if (__DEV__) {
        console.warn('[TripMap] Applying landmark pins:', {
          raw: rawLandmarks.length,
          enriched: enrichedPlaces.length,
          pins: landmarkPins.length,
          preservedItinerary: itineraryPins.length,
        });
      }
      return [...userSearch, ...itineraryPins, ...landmarkPins];
    });
    // `annotatePlacesForTrip` only depends on itinerary titles/dates + trip range — not Firestore reference churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawLandmarks, itineraryLandmarkAnnotationKey, tripLandmarkAnnotationKey, selectedTripId]);

  const landmarkRetryAnchorKey = useMemo(() => {
    const isValid = (lat?: number, lng?: number) =>
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180 &&
      !(lat === 0 && lng === 0);

    const fromPins = mapPins
      .filter((p) => p.type === 'itinerary')
      .map((p) => p.coordinates)
      .filter((c): c is { latitude: number; longitude: number } =>
        c != null && isValid(c.latitude, c.longitude),
      )
      .slice(0, 6)
      .map((c) => `${c.latitude.toFixed(2)},${c.longitude.toFixed(2)}`);
    if (fromPins.length > 0) return fromPins.join('|');

    const fromItems = itineraryItems
      .map((it) => it.coordinates)
      .filter((c): c is { latitude: number; longitude: number } =>
        c != null && isValid(c.latitude, c.longitude),
      )
      .slice(0, 6)
      .map((c) => `${c.latitude.toFixed(2)},${c.longitude.toFixed(2)}`);
    return fromItems.join('|');
  }, [itineraryItems, mapPins]);

  // If the first landmark fetch happened before itinerary geocodes were available,
  // retry once after we have concrete itinerary anchors for proximity.
  useEffect(() => {
    if (!selectedTripId) return;
    if (!landmarkFetchCityLabel) return;
    if (landmarkSource === 'baseline') return;
    if (landmarksRefreshing) return;
    if (pinsLoading) return;
    if (rawLandmarks.length > 0) return;
    if (!landmarkRetryAnchorKey) return;

    const gateKey = `${selectedTripId}:${landmarkFetchCityLabel.trim().toLowerCase()}`;
    if (landmarkEmptyDiscoveryRetryScheduledRef.current.has(gateKey)) return;
    landmarkEmptyDiscoveryRetryScheduledRef.current.add(gateKey);
    if (landmarkReloadCoalesceTimerRef.current) {
      clearTimeout(landmarkReloadCoalesceTimerRef.current);
    }
    landmarkReloadCoalesceTimerRef.current = setTimeout(() => {
      landmarkReloadCoalesceTimerRef.current = null;
      setLandmarksReloadNonce((n) => n + 1);
    }, 360);
  }, [
    landmarkFetchCityLabel,
    landmarkRetryAnchorKey,
    landmarkSource,
    landmarksRefreshing,
    pinsLoading,
    rawLandmarks.length,
    selectedTripId,
  ]);

  // If discovery fetched too early (before itinerary anchors finished) and ended at zero,
  // run one more automatic retry after itinerary pinning settles.
  useEffect(() => {
    if (!selectedTripId) return;
    if (!landmarkFetchCityLabel) return;
    if (landmarkSource === 'baseline') return;
    if (landmarksRefreshing) return;
    if (rawLandmarks.length > 0) return;
    if (pinsLoading) return;

    const itineraryPinCount = mapPins.filter((p) => p.type === 'itinerary').length;
    if (itineraryPinCount <= 0) return;

    const gateKey = `${selectedTripId}:${landmarkFetchCityLabel.trim().toLowerCase()}`;
    if (landmarkEmptyDiscoveryRetryScheduledRef.current.has(gateKey)) return;
    landmarkEmptyDiscoveryRetryScheduledRef.current.add(gateKey);
    if (landmarkReloadCoalesceTimerRef.current) {
      clearTimeout(landmarkReloadCoalesceTimerRef.current);
    }
    landmarkReloadCoalesceTimerRef.current = setTimeout(() => {
      landmarkReloadCoalesceTimerRef.current = null;
      setLandmarksReloadNonce((n) => n + 1);
    }, 360);
  }, [
    landmarkFetchCityLabel,
    landmarkSource,
    landmarksRefreshing,
    mapPins,
    pinsLoading,
    rawLandmarks.length,
    selectedTripId,
  ]);

  // Last-mile reliability: if landmarks are still empty, retry automatically a few times
  // so new users discover landmarks without manually tapping refresh.
  useEffect(() => {
    if (!selectedTripId) return;
    const city = landmarkFetchCityLabel?.trim();
    if (!city) return;
    if (landmarkSource === 'baseline') return;
    if (pinsLoading) return;
    if (landmarksRefreshing) return;
    if (rawLandmarks.length > 0) return;

    const key = `${selectedTripId}:${city.toLowerCase()}`;
    const attempts = landmarkAutoBackfillAttemptsRef.current.get(key) ?? 0;
    if (attempts >= 3) return;

    if (landmarkAutoBackfillTimerRef.current) {
      clearTimeout(landmarkAutoBackfillTimerRef.current);
    }
    // Backoff: 2s, 4s, 8s. Keeps cost bounded while handling timing/network races.
    const delayMs = Math.min(8000, 2000 * Math.pow(2, attempts));
    landmarkAutoBackfillTimerRef.current = setTimeout(() => {
      landmarkAutoBackfillAttemptsRef.current.set(key, attempts + 1);
      // Ensure this retry can perform a live fetch even if an earlier attempt set the guard.
      const cityPrefix = `${city.toLowerCase()}::`;
      for (const fetchKey of Array.from(landmarkLiveFetchAttemptedRef.current)) {
        if (fetchKey.startsWith(cityPrefix)) {
          landmarkLiveFetchAttemptedRef.current.delete(fetchKey);
        }
      }
      if (landmarkReloadCoalesceTimerRef.current) {
        clearTimeout(landmarkReloadCoalesceTimerRef.current);
      }
      landmarkReloadCoalesceTimerRef.current = setTimeout(() => {
        landmarkReloadCoalesceTimerRef.current = null;
        setLandmarksReloadNonce((n) => n + 1);
      }, 0);
    }, delayMs);

    return () => {
      if (landmarkAutoBackfillTimerRef.current) {
        clearTimeout(landmarkAutoBackfillTimerRef.current);
        landmarkAutoBackfillTimerRef.current = null;
      }
    };
  }, [
    landmarkFetchCityLabel,
    landmarkSource,
    landmarksRefreshing,
    pinsLoading,
    rawLandmarks.length,
    selectedTripId,
  ]);

  // ~11m quantization: avoid rebuilding the entire pin list (native map blink) on sub-meter GPS jitter.
  const userLocationPinKey = useMemo(() => {
    if (!currentLocation) return '';
    return `${currentLocation.latitude.toFixed(4)},${currentLocation.longitude.toFixed(4)}`;
  }, [currentLocation?.latitude, currentLocation?.longitude]);

  // User location pin
  useEffect(() => {
    if (!userLocationPinKey) {
      setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'user'));
      return;
    }
    const loc = currentLocationRef.current;
    if (!loc) {
      setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'user'));
      return;
    }

    setMapPins((prevPins) => {
      const existing = prevPins.find((p) => p.type === 'user');
      if (
        existing?.coordinates &&
        Math.abs(existing.coordinates.latitude - loc.latitude) < 0.0002 &&
        Math.abs(existing.coordinates.longitude - loc.longitude) < 0.0002
      ) {
        return prevPins;
      }
      const otherPins = prevPins.filter((p) => p.type !== 'user');
      return [
        ...otherPins,
        {
          id: 'user-location',
          coordinates: loc,
          title: 'Your Location',
          type: 'user',
        },
      ];
    });
  }, [userLocationPinKey]);

  // Navigation prompts with adaptive update frequency
  useEffect(() => {
    if (!enablePrompts || !enableLocation) {
      setNavigationPrompt(null);
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    const generatePrompt = async () => {
      if (!currentLocation || itineraryItems.length === 0) {
        setNavigationPrompt(null);
        return;
      }

      try {
        const prompt = await generateNavigationPrompt(currentLocation, itineraryItems);
        setNavigationPrompt(prompt);

        if (prompt && notificationsEnabled) {
          // Use gated notification with preference/dedupe/rate-limit checks
          const tripId = selectedTrip?.id;
          const itemId = prompt.nextActivity?.id || 'unknown';
          await sendGatedNotification(
            {
              category: 'itinerary',
              channel: 'push',
              tripId,
              priority: prompt.leaveInMinutes <= 5 ? 'high' : 'medium',
              dedupeKey: buildDedupeKey.navigation(tripId || 'global', itemId),
              cooldownMs: DEFAULT_COOLDOWNS.navigation,
            },
            () => sendNavigationPrompt(prompt)
          );
        }

        // Adaptive update frequency based on time until departure
        // More frequent updates when approaching departure time
        let updateInterval = 5 * 60 * 1000; // Default: 5 minutes

        if (prompt) {
          const minutesUntilDeparture = prompt.leaveInMinutes;

          if (minutesUntilDeparture < 5) {
            // Very close (< 5 min): Update every 30 seconds
            updateInterval = 30 * 1000;
          } else if (minutesUntilDeparture < 15) {
            // Close (< 15 min): Update every minute
            updateInterval = 60 * 1000;
          } else if (minutesUntilDeparture < 30) {
            // Approaching (< 30 min): Update every 2 minutes
            updateInterval = 2 * 60 * 1000;
          } else if (minutesUntilDeparture < 60) {
            // Soon (< 60 min): Update every 3 minutes
            updateInterval = 3 * 60 * 1000;
          }
          // Otherwise use default 5 minutes
        }

        // Clear existing interval and set new one with adaptive frequency
        if (intervalId) {
          clearInterval(intervalId);
        }
        intervalId = setInterval(generatePrompt, updateInterval);
      } catch (error) {
        console.error('[TripMap] Error generating navigation prompt:', error);
        // On error, use default interval
        if (intervalId) {
          clearInterval(intervalId);
        }
        intervalId = setInterval(generatePrompt, 5 * 60 * 1000);
      }
    };

    // Initial generation
    generatePrompt();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enablePrompts, enableLocation, currentLocation, itineraryItems, notificationsEnabled]);

  // Nearby landmark notifications
  useEffect(() => {
    if (!enablePrompts || !enableLocation) {
      return;
    }

    const checkLandmarks = async () => {
      if (!currentLocation || landmarks.length === 0 || !notificationsEnabled) {
        return;
      }

      const hasNoActivity = itineraryItems.every((item) => new Date(item.startDate) > new Date());
      await checkNearbyLandmarks(currentLocation, landmarks, hasNoActivity);
    };

    if (currentLocation && landmarks.length > 0 && notificationsEnabled) {
      const interval = setInterval(checkLandmarks, 2 * 60 * 1000);
      checkLandmarks();
      return () => clearInterval(interval);
    }
  }, [enablePrompts, enableLocation, currentLocation, landmarks, notificationsEnabled, itineraryItems]);

  // Keep local places cache fresh
  useEffect(() => {
    const updateCache = async () => {
      const activeDestinationLabel = selectedCityDestination?.city || selectedTrip?.destination || selectedTrip?.name;
      if (!activeDestinationLabel) return;

      try {
        await updatePlacesCacheOnWifi(activeDestinationLabel);
      } catch (error) {
        console.error('[TripMap] Error updating places cache:', error);
      }
    };

    if (selectedCityDestination?.city || selectedTrip?.destination || selectedTrip?.name) {
      updateCache();
    }
  }, [selectedCityDestination?.city, selectedTrip?.destination, selectedTrip?.name]);

  const [searchCenter, setSearchCenter] = useState<LocationCoordinates | null>(null);

  const handleSearchSelect = useCallback((place: GeocodeResult) => {
    const coordinates = {
      latitude: place.coordinates[1],
      longitude: place.coordinates[0],
    };

    const pin: MapPin = {
      id: `search-${Date.now()}`,
      coordinates,
      title: place.placeName,
      type: 'search',
    };

    setMapPins((prevPins) => {
      const otherPins = prevPins.filter((p) => p.type !== 'search');
      return [...otherPins, pin];
    });

    // Set search center to force map to center on selected place
    setSearchCenter(coordinates);

    if (__DEV__) {
      console.log(`[TripMap] Search place selected: ${place.placeName} at ${coordinates.latitude}, ${coordinates.longitude}`);
    }
  }, []);

  const handleClearSearchResults = useCallback(() => {
    setSearchCenter(null);
    setMapPins((prevPins) => prevPins.filter((p) => p.type !== 'search'));
  }, []);

  const handlePinPress = useCallback((pin: MapPin) => {
    console.log('[TripMap] Pin pressed:', pin);
  }, []);

  const handleToggleNotifications = useCallback(async () => {
    if (!notificationsEnabled) {
      const granted = await requestNotificationPermissions();
      setNotificationsEnabled(granted);
      if (!granted) {
        Alert.alert(
          'Permission Required',
          'Enable notifications to receive travel-time nudges and nearby highlights. You can continue using maps without alerts.',
          [
            { text: 'Not now', style: 'cancel' },
            { text: 'Open Settings', onPress: () => void Linking.openSettings() },
          ],
        );
      }
    } else {
      setNotificationsEnabled(false);
    }
  }, [notificationsEnabled]);

  const handleGetDirections = useCallback(() => {
    if (!navigationPrompt) return;

    const { nextActivity } = navigationPrompt;
    const address = nextActivity.address || nextActivity.location;

    if (!address) {
      return;
    }

    const encodedAddress = encodeURIComponent(address);
    const url = Platform.select({
      ios: `maps://maps.apple.com/?daddr=${encodedAddress}`,
      android: `google.navigation:q=${encodedAddress}`,
      default: `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    });

    if (!url) {
      return;
    }

    Linking.openURL(url).catch((err) => {
      console.error('[TripMap] Error opening directions:', err);
      Alert.alert('Error', 'Could not open directions app.');
    });
  }, [navigationPrompt]);

  const looksLikeUsableDestinationLabel = useCallback(
    (label: string | undefined | null): label is string => {
      if (!label) return false;
      const trimmed = label.trim();
      if (!trimmed) return false;
      // Avoid using generic trip titles ("Beta Test") as destinations.
      const lower = trimmed.toLowerCase();
      if (/\b(beta|test|sample|demo)\b/.test(lower)) return false;
      if (trimmed.length > 80) return false;

      // Known coordinate mapping or country mapping is a strong signal.
      if (getDestinationCoordinates(trimmed)) return true;
      if (getCountryCodeFromDestination(trimmed)) return true;
      // "City, Country" format is typically geocodable.
      if (trimmed.includes(',')) return true;
      return false;
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const geocodeDestination = async () => {
      // Clear previous trip's center when trip/destination changes so we don't show e.g. Detroit for a New York trip
      setPreviousDestinationCenter(null);

      // IMPORTANT: for multi-city trips, `selectedTrip.destination` can be a composite label
      // like "London, Rome & Paris" which is both hard to geocode and biases results to the
      // first city. For map-centering geocoding we must prefer an actual *single* city.
      const rawCandidates = [
        selectedCityDestination?.city,
        tripDestinations?.[0]?.city,
        selectedTrip?.destinations?.[0]?.city,
        inferDestinationLabelFromItinerary(itineraryItems),
        selectedTrip?.destination,
        selectedTrip?.name,
      ].filter(Boolean) as string[];

      // Prefer a candidate that looks like a real place label; only then fall back to "first non-empty".
      let destinationLabel =
        rawCandidates.find((c) => looksLikeUsableDestinationLabel(c)) || rawCandidates[0];
      if (!destinationLabel) {
        setDestinationCenter(null);
        setPreviousDestinationCenter(null);
        return;
      }

      destinationLabel = enrichCityLabelWithCountryParts(destinationLabel, {
        country:
          selectedCityDestination?.country ||
          tripDestinations?.[0]?.country ||
          selectedTrip?.destinations?.[0]?.country,
        fullDestination: selectedTrip?.destination,
      });

      const parsedDestination = resolveTripDestinationLabel(
        selectedCityDestination ||
          tripDestinations?.[0] ||
          selectedTrip?.destinations?.[0] ||
          null,
        selectedTrip?.destination,
      );
      const destinationCountryCode =
        parsedDestination?.countryCode || getCountryCodeFromDestination(destinationLabel);

      // Check if trip has coordinates in destinations array (fastest path)
      if (selectedTrip?.destinations && selectedTrip.destinations.length > 0) {
        const firstDest = selectedTrip.destinations[0];
        if (firstDest.coordinates) {
          const coords = {
            latitude: firstDest.coordinates.latitude,
            longitude: firstDest.coordinates.longitude,
          };
          setDestinationCenter(coords);
          setPreviousDestinationCenter(coords);
          return;
        }
      }

      // Try to get cached geocode result first (instant display)
      let anchorResolved = false;
      try {
        const { getCachedGeocode } = await import('@/utils/geocodeCache');
        const cacheOptions = destinationCountryCode ? { country: destinationCountryCode } : undefined;
        const cached = await getCachedGeocode(destinationLabel, cacheOptions);
        if (cached) {
          const [lng, lat] = cached.coordinates;
          if (
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180 &&
            !(lat === 0 && lng === 0) &&
            coordsWithinCountryBounds(destinationCountryCode, lat, lng) &&
            validateGeocodeResult({ lat, lng }, destinationLabel, 1500)
          ) {
            setDestinationCenter({ latitude: lat, longitude: lng });
            setPreviousDestinationCenter({ latitude: lat, longitude: lng });
            anchorResolved = true;
            if (__DEV__) {
              console.log(`[TripMap] Using cached geocode for "${destinationLabel}": ${lat}, ${lng}`);
            }
          }
        }
      } catch (error) {
        if (__DEV__) {
          console.warn('[TripMap] Error reading geocode cache:', error);
        }
      }

      const tryItineraryAddressAnchor = async (): Promise<boolean> => {
        for (const it of itineraryItems) {
          const addr = (it.address || it.location || '').trim();
          if (!addr || addr.split(',').length < 2) continue;
          const addrResult = await geocodeAddress(
            addr,
            destinationCountryCode ? { country: destinationCountryCode } : undefined,
          ).catch(() => null);
          if (!addrResult?.coordinates?.length) continue;
          const [addrLng, addrLat] = addrResult.coordinates;
          if (
            addrLat >= -90 &&
            addrLat <= 90 &&
            addrLng >= -180 &&
            addrLng <= 180 &&
            coordsWithinCountryBounds(destinationCountryCode, addrLat, addrLng)
          ) {
            if (__DEV__) {
              console.log(`[TripMap] Using itinerary address anchor for destination: ${addr}`);
            }
            setDestinationCenter({ latitude: addrLat, longitude: addrLng });
            setPreviousDestinationCenter({ latitude: addrLat, longitude: addrLng });
            return true;
          }
        }
        return false;
      };

      // Geocode in background to refresh/update cache
      try {
        const geocodeOptions: { country?: string } = {};
        if (destinationCountryCode) geocodeOptions.country = destinationCountryCode;

        const result = await geocodeAddress(destinationLabel, geocodeOptions);
        if (!cancelled && result) {
          const [lng, lat] = result.coordinates;
          if (
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180 &&
            !(lat === 0 && lng === 0) &&
            coordsWithinCountryBounds(destinationCountryCode, lat, lng) &&
            validateGeocodeResult({ lat, lng }, destinationLabel, 1500)
          ) {
            if (__DEV__) {
              console.log(
                `[TripMap] Geocoded destination "${destinationLabel}" to: ${lat}, ${lng} (${result.placeName})`,
              );
            }
            setDestinationCenter({ latitude: lat, longitude: lng });
            setPreviousDestinationCenter({ latitude: lat, longitude: lng });
            anchorResolved = true;
          }
        }

        if (!cancelled && !anchorResolved) {
          anchorResolved = await tryItineraryAddressAnchor();
        }

        if (!cancelled && !anchorResolved) {
          if (__DEV__) {
            console.warn(`[TripMap] Could not geocode destination: ${destinationLabel}`);
          }
          setDestinationCenter(null);
        }
      } catch (error) {
        console.error('[TripMap] Error geocoding destination:', error);
        // Keep previous location if available, don't clear it
        if (!cancelled && !previousDestinationCenter) {
          setDestinationCenter(null);
        }
      }
    };

    geocodeDestination();

    return () => {
      cancelled = true;
    };
  }, [
    selectedCityDestination?.city,
    tripDestinations,
    selectedTrip?.destination,
    selectedTrip?.destinations,
    selectedTrip?.name,
    itineraryItems,
    inferDestinationLabelFromItinerary,
  ]);

  const mapCenterRaw = useMemo(() => {
    // Priority: search center (when user selects from search) > first pin > trip destination > previous destination (while geocoding) > current location > default (world view)
    // This ensures the map defaults to the trip's location, not the user's current location
    if (searchCenter) {
      return searchCenter;
    }
    const isValidCoord = (c?: { latitude: number; longitude: number } | null) =>
      !!c &&
      Number.isFinite(c.latitude) &&
      Number.isFinite(c.longitude) &&
      Math.abs(c.latitude) <= 90 &&
      Math.abs(c.longitude) <= 180 &&
      !(c.latitude === 0 && c.longitude === 0);

    const destinationAnchor =
      isValidCoord(selectedCityDestination?.coordinates)
        ? {
            latitude: selectedCityDestination!.coordinates!.latitude,
            longitude: selectedCityDestination!.coordinates!.longitude,
          }
        : isValidCoord(selectedTrip?.destinations?.[0]?.coordinates)
          ? {
              latitude: selectedTrip!.destinations![0]!.coordinates!.latitude,
              longitude: selectedTrip!.destinations![0]!.coordinates!.longitude,
            }
          : isValidCoord(destinationCenter)
            ? destinationCenter
            : null;

    const MAX_PIN_CENTER_KM_FROM_DESTINATION = 400;
    if (mapPins.length > 0) {
      // Ignore user/search pins when deciding the default center.
      // Otherwise the map will center on the device location even when itinerary pins exist.
      const candidatePins = mapPins.filter(
        (p) => p.type === 'itinerary' || p.type === 'landmark' || p.type === 'search',
      );
      const anchoredPins = destinationAnchor
        ? candidatePins.filter(
            (p) =>
              haversineDistance(
                destinationAnchor,
                p.coordinates,
              ) <= MAX_PIN_CENTER_KM_FROM_DESTINATION,
          )
        : candidatePins;
      const preferred =
        anchoredPins[0] ||
        // If all pins are outliers vs destination, ignore them for center fallback.
        null;
      if (preferred) return preferred.coordinates;
    }

    // Fast path: if the trip destinations already include coordinates, center immediately
    // without waiting for async geocoding/cache reads.
    const destCoords =
      selectedCityDestination?.coordinates ||
      selectedTrip?.destinations?.[0]?.coordinates ||
      null;
    if (
      destCoords &&
      Number.isFinite(destCoords.latitude) &&
      Number.isFinite(destCoords.longitude) &&
      Math.abs(destCoords.latitude) <= 90 &&
      Math.abs(destCoords.longitude) <= 180 &&
      !(destCoords.latitude === 0 && destCoords.longitude === 0)
    ) {
      return {
        latitude: destCoords.latitude,
        longitude: destCoords.longitude,
      };
    }

    if (destinationCenter) {
      return destinationCenter;
    }

    // Prefer current trip's known destination over a previous trip's center (avoids showing Detroit
    // for a New York/Vietnam trip when geocoding failed and previousDestinationCenter was from another trip).
    const destinationCandidates = [
      selectedCityDestination?.city,
      tripDestinations?.[0]?.city,
      selectedTrip?.destinations?.[0]?.city,
      inferDestinationLabelFromItinerary(itineraryItems),
      selectedTrip?.destination,
      selectedTrip?.name,
    ].filter(Boolean) as string[];

    const destinationLabel =
      destinationCandidates.find((c) => looksLikeUsableDestinationLabel(c)) || destinationCandidates[0];
    if (destinationLabel) {
      const approx = getDestinationCoordinates(destinationLabel);
      if (approx) {
        return { latitude: approx.lat, longitude: approx.lng };
      }
    }

    // Show previous destination only while geocoding and only when we have no known coords for this trip
    if (previousDestinationCenter && isGeocoding) {
      return previousDestinationCenter;
    }

    // Do not fall back to current location for default trip map center.
    // It makes failed/incorrect pin geocodes look like the trip moved to the user.
    // If we have a trip destination but no center yet, geocoding might be in progress
    // Return null to indicate we should wait, or use a very neutral default
    // Avoid defaulting to Ethiopia (20, 0) or any specific location
    // World view center (equator, prime meridian) is most neutral
    return { latitude: 0, longitude: 0 };
  }, [
    searchCenter,
    mapPins,
    destinationCenter,
    previousDestinationCenter,
    isGeocoding,
    selectedCityDestination?.city,
    selectedCityDestination?.coordinates,
    tripDestinations,
    selectedTrip?.destination,
    selectedTrip?.destinations,
    selectedTrip?.name,
    itineraryItems,
    inferDestinationLabelFromItinerary,
  ]);

  const mapCenter = useMemo(() => {
    const next = mapCenterRaw;
    if (!next || (next.latitude === 0 && next.longitude === 0)) {
      mapCenterStableRef.current = null;
      return next;
    }
    const prev = mapCenterStableRef.current;
    if (
      prev &&
      Math.abs(prev.latitude - next.latitude) < 1e-7 &&
      Math.abs(prev.longitude - next.longitude) < 1e-7
    ) {
      return prev;
    }
    mapCenterStableRef.current = next;
    return next;
  }, [mapCenterRaw]);

  // Clear search center when pins change (so map doesn't stay locked to search result)
  useEffect(() => {
    if (searchCenter && mapPins.length > 0) {
      // Keep search center for a bit, then clear it so map can follow other pins
      const timer = setTimeout(() => {
        setSearchCenter(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [searchCenter, mapPins]);

  const tripOptions = useMemo(() => buildTripSelectOptions(tripsForSelection), [tripsForSelection]);

  useEffect(() => {
    pinsProgressUpdatedAtRef.current = Date.now();
  }, [pinsProgress.total, pinsProgress.processed]);

  useEffect(() => {
    if (!pinsLoading) {
      if (pinsLoadingWatchdogRef.current) {
        clearTimeout(pinsLoadingWatchdogRef.current);
        pinsLoadingWatchdogRef.current = null;
      }
      if (isUiDebugLoggingEnabled()) {
        console.log('[TripMap] Pins loading complete');
      }
      return;
    }

    if (pinsLoadingWatchdogRef.current) {
      clearTimeout(pinsLoadingWatchdogRef.current);
    }

    const watchdogMs = 45_000;
    const stallMs = 25_000;
    pinsLoadingWatchdogRef.current = setTimeout(() => {
      const stalledForMs = Date.now() - pinsProgressUpdatedAtRef.current;
      if (pinsLoading && stalledForMs >= stallMs) {
        if (__DEV__) {
          console.warn('[TripMap] Pins loading watchdog triggered - clearing stalled state', {
            stalledForMs,
            pinsProgress,
            isGeocoding,
            itineraryItemsLoading,
            tripsLoading,
          });
        }
        logIosGeocode('pins-loading-stall', {
          stalledForMs,
          isGeocoding,
          pinsProgress,
          runKey: geocodeRunKeyRef.current,
        });
        try {
          geocodeAbortRef.current?.abort();
        } catch {
          // ignore
        } finally {
          geocodeAbortRef.current = null;
        }
        setIsGeocoding(false);
        setIsGeocodingPaused(false);
        setPinsProgress({ total: 0, processed: 0 });
        geocodeRunKeyRef.current = null;
        geocodeRunStartedAtRef.current = null;
        geocodeNeedsRetryRef.current = true;
        if (appStateRef.current === 'active') {
          scheduleGeocodeRetry('pins-watchdog');
        }
      }
    }, watchdogMs);

    return () => {
      if (pinsLoadingWatchdogRef.current) {
        clearTimeout(pinsLoadingWatchdogRef.current);
        pinsLoadingWatchdogRef.current = null;
      }
    };
  }, [pinsLoading, pinsProgress, isGeocoding, itineraryItemsLoading, tripsLoading, scheduleGeocodeRetry, logIosGeocode]);

  return {
    trips,
    tripsLoading,
    retryLoadTrips,
    tripLoadDebug,
    tripOptions,
    selectedTrip,
    selectedTripId,
    setSelectedTripId,
    tripDestinations,
    isMultiCityTrip,
    selectedCityIndex,
    setSelectedCityIndex,
    selectedCityDestination,
    fullTripBounds,
    tripDays: undefined as any,
    selectedDayIndex: undefined as any,
    setSelectedDayIndex: undefined as any,
    selectedDay: undefined as any,
    selectedDayActivities: undefined as any,
    handleSwipeDayPrev: undefined as any,
    handleSwipeDayNext: undefined as any,
    missingItineraryItems: [] as any[],
    missingPinsByDay: {} as any,
    selectedDayMissingPins: 0 as any,
    itineraryItems,
    itineraryItemsLoading,
    mapPins,
    mapCenter,
    currentLocation,
    landmarks,
    landmarksRawCount: rawLandmarks.length,
    showingCachedLandmarks: landmarkSource === 'cache' && rawLandmarks.length > 0,
    showingBaselineLandmarks: landmarkSource === 'baseline' && rawLandmarks.length > 0,
    landmarkDataSource: landmarkSource,
    landmarkFetchCityLabel,
    landmarkFetchProximity,
    navigationPrompt,
    showNavigationPrompt,
    setShowNavigationPrompt,
    showOfflineDownload,
    setShowOfflineDownload,
    isDownloaded,
    setIsDownloaded,
    notificationsEnabled,
    handleToggleNotifications,
    handleSearchSelect,
    handleClearSearchResults,
    handlePinPress,
    handleGetDirections,
    geocodeStatus,
    isGeocoding,
    isGeocodingPaused,
    pinsLoading,
    pinsProgress,
    geocodeRateLimited,
    refreshPins,
    nudgePinsForDisplay,
    refreshLandmarkSuggestions,
    landmarksRefreshing,
  };
}

const annotatePlacesForTrip = (
  places: Place[],
  itineraryItems: ItineraryItem[],
  trip: Trip | null,
): Place[] => {
  if (!places.length) {
    return [];
  }

  if (!trip) {
    return places;
  }

  const plannedTitles = new Set(
    itineraryItems
      .map((item) => item.title?.toLowerCase().trim())
      .filter(Boolean) as string[],
  );

  const unplannedDayCount = getUnplannedDayCount(trip, itineraryItems);
  const candidateMustSee = places
    .filter(
      (place) =>
        (place.category === 'landmark' || place.category === 'attraction') &&
        !plannedTitles.has(place.name.toLowerCase()),
    )
    .sort((a, b) => (b.popularityScore ?? 0) - (a.popularityScore ?? 0));

  const mustSeeCount = Math.min(
    candidateMustSee.length,
    Math.max(2, Math.min(6, unplannedDayCount * 2)),
  );

  const mustSeeIds = new Set(candidateMustSee.slice(0, mustSeeCount).map((place) => place.id));

  return places.map((place) => {
    if (mustSeeIds.has(place.id)) {
      const tags = new Set(place.tags || []);
      tags.add('must-see');
      return {
        ...place,
        isMustSee: true,
        tags: Array.from(tags),
        trendingReason: place.trendingReason || 'Great fit for an open day in your plan',
      };
    }

    if (place.isMustSee) {
      return place;
    }

    return {
      ...place,
      isMustSee: false,
    };
  });
};

const getUnplannedDayCount = (trip: Trip | null, itineraryItems: ItineraryItem[]): number => {
  if (!trip?.startDate || !trip?.endDate) {
    return 0;
  }

  const dayKeys = enumerateTripDayKeys(trip);
  const plannedDays = new Set(
    itineraryItems.map((item) => formatDateKey(item.startDate ?? new Date())),
  );

  return dayKeys.filter((day) => !plannedDays.has(day)).length;
};

const enumerateTripDayKeys = (trip: Trip): string[] => {
  const start = normalizeDate(trip.startDate);
  const end = normalizeDate(trip.endDate);
  const keys: string[] = [];

  if (!start || !end) {
    return keys;
  }

  const cursor = new Date(start);
  while (cursor <= end) {
    keys.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys;
};

const formatDateKey = (value: Date | string): string => {
  const date = value instanceof Date ? value : new Date(value);
  return date.toISOString().split('T')[0];
};

const normalizeDate = (value: Date | string | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
};

